// --- Dummy-Implementierung für cleanUndefined ---
function cleanUndefined(obj: any) {
    if (!obj || typeof obj !== 'object') return obj;
    return Object.fromEntries(Object.entries(obj).filter(([_, v]) => v !== undefined));
}
// requestConfirm wird in der Komponente per setConfirmDialog bereitgestellt (echter AlertDialog).
import type { AppUser, Customer, ConfirmDialogConfig } from './types/interfaces';
import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
// Wird von Vite in vite.config.ts automatisch generiert (inkl. package.json-Version, Buildzeit, Git-Hash)
declare const __APP_VERSION__: string;
// addLog wird in der App-Komponente definiert und schreibt in Firestore (logs)

// Dummy-Komponenten und Hilfsfunktionen VOR der ersten Verwendung
import DashboardView from './components/views/DashboardView';
import { useAuth } from "./contexts/AppContexts";
import KundenKartei from './components/KundenKartei';
import RosterView from './components/views/RosterView';
import BestandsView from './components/views/BestandsView';
import BoniRechnerView from './components/views/BoniRechnerView';
import BoniTabellenAdminView from './components/views/BoniTabellenAdminView';
import CustomersView from './components/views/CustomersView';
import PhonebookView from './components/views/PhonebookView';
import TeamView from './components/views/TeamView';
import GoalsView from './components/views/GoalsView';
import BirthdaysView from './components/views/BirthdaysView';
import ExpirationView from './components/views/ExpirationView';
import VerlaengerungView from './components/views/VerlaengerungView';
import ProvisionsView from './components/views/ProvisionsView';
import TasksListView from './components/views/TasksListView';
import TemplateManagerView from './components/views/TemplateManagerView';
import PasswordsView from './components/views/PasswordsView';
import ActivityLogView from './components/views/ActivityLogView';
import StatsView from './components/views/StatsView';
import { PageLayout, PageHeader, PageCard } from './components/PageLayout';
import { ConfirmDialog } from './components/ConfirmDialog';
import { GlobalSearch } from './components/GlobalSearch';
import SettingsView from './components/views/SettingsView';
import LoginView from './components/views/LoginView';
import EmployeeEditForm from './components/EmployeeEditForm';
import { CRMHeader } from './components/CRMHeader';
import { CustomerDetailPanel } from './components/CustomerDetailPanel';
const Toast = (props: any) => <div>Toast: {props.message}</div>;
import {
    Gift, LogOut, Menu, ChevronRight, ClipboardList, Phone, User, FileText,
    Target, Calendar, CheckCircle, Shield, Circle, RefreshCw, Mail, Printer,
    Plus, Eye, Settings, Users, LayoutDashboard, UserCog,
    CalendarDays, KeyRound, Contact2, Loader2, ArrowRight, ActivitySquare,
    ChevronDown, Activity, Trash2, X, Cake, BarChart3, Table2, Moon, Sun,
    Mail as MailIcon, Upload, Calculator, DollarSign, TrendingUp, FileSpreadsheet, Lock,
    AlertCircle, Save, RotateCcw, Clock
} from 'lucide-react';

const IOSInput = (props: any) => (
    <input
        {...props}
        className={`w-full px-4 py-3 rounded-xl border transition-all outline-none focus:ring-2 focus:ring-primary/20 ${props.className || ''} ${document.documentElement.classList.contains('dark')
                ? 'bg-white/10 border-white/10 text-white placeholder:text-white/30 focus:border-primary/50'
                : 'bg-slate-50 border-slate-200 text-slate-900 focus:border-primary'
            }`}
    />
);
const IOSButton = (props: any) => {
    const isGhost = props.variant === 'ghost';
    const isDark = document.documentElement.classList.contains('dark');
    return (
        <button
            {...props}
            className={`min-h-[44px] px-6 py-2.5 rounded-xl font-bold transition-all active:scale-[0.98] flex items-center justify-center gap-2 ${props.className || ''} ${isGhost
                    ? (isDark ? 'bg-white/5 text-white hover:bg-white/10' : 'bg-slate-100 text-slate-600 hover:bg-slate-200')
                    : (isDark ? 'bg-primary text-white shadow-glow hover:brightness-110' : 'bg-primary text-white shadow-lg hover:brightness-110')
                }`}
        >
            {props.children}
        </button>
    );
};
function analyzeGaps(contracts: any[]) { return []; }

function generatePDF(customer: any, notes: any[] = [], includeNotes: boolean = false) {
    if (!customer) return;
    try {
        const doc = new jsPDF();
        const title = `${customer.vorname || ''} ${customer.name || ''}`.trim() || (customer.adp || 'Kunde');
        const contracts = customer.verträge || customer.vertraege || [];

        const pageWidth = doc.internal.pageSize.getWidth();

        // Farbpalette passend zum App-Design
        const primary = { r: 8, g: 47, b: 73 };     // dunkel-cyan/blau
        const primaryLight = { r: 224, g: 242, b: 254 }; // helles Blau

        // Header-Balken
        doc.setFillColor(primary.r, primary.g, primary.b);
        doc.rect(0, 0, pageWidth, 24, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(14);
        doc.text('Kundenübersicht', 14, 10);
        doc.setFontSize(16);
        doc.text(title, 14, 20);

        // Basis-Textfarbe zurücksetzen
        doc.setTextColor(17, 24, 39);

        doc.setFontSize(10);
        const addressLine = [customer.strasse, customer.plz, customer.ort].filter(Boolean).join(' ');

        // Kundeninfo-Box
        let y = 32;
        doc.setFillColor(primaryLight.r, primaryLight.g, primaryLight.b);
        doc.setDrawColor(148, 163, 184); // Slate-400
        doc.roundedRect(12, y - 6, pageWidth - 24, 20, 3, 3, 'FD');

        doc.setFontSize(10);
        doc.text(`Name: ${title}`, 16, y);
        if (addressLine) doc.text(`Adresse: ${addressLine}`, 16, y + 5);
        doc.text(`ADP: ${customer.adp || '–'}`, 16, y + 10);
        if (customer.zustMA) doc.text(`Betreuer: ${customer.zustMA}`, 16, y + 15);

        // Kennzahlen ermitteln
        let totalJab = 0;
        const spartenSet = new Set<string>();
        const rows = contracts.map((v: any) => [
            v.sparte || '',
            v.gesellschaft || '',
            v.vsnr || v.vsNr || '',
            v.beginn || '',
            v.ablauf || '',
            String(v.beitrag ?? v.JAB ?? '')
        ]);
        contracts.forEach((v: any) => {
            if (v.sparte) spartenSet.add(v.sparte);
            const raw = v.beitrag ?? v.JAB;
            const num = typeof raw === 'number' ? raw : parseFloat(String(raw || '0').replace(/[^\d,.-]/g, '').replace(',', '.'));
            if (!isNaN(num)) totalJab += num;
        });

        // kleine Zusammenfassung-Box
        const summaryY = y + 26;
        doc.setFontSize(11);
        doc.text('Übersicht', 14, summaryY);
        doc.setFontSize(9);
        doc.text(`Verträge: ${contracts.length}`, 14, summaryY + 5);
        doc.text(`Sparten: ${spartenSet.size || 0}`, 60, summaryY + 5);
        if (totalJab > 0) {
            doc.text(`Summe Beiträge (Jahr): ${formatMoney(totalJab)}`, 14, summaryY + 10);
        }

        autoTable(doc, {
            head: [['Sparte', 'Gesellschaft', 'VSNR', 'Beginn', 'Ablauf', 'Jahresbeitrag']],
            body: rows,
            startY: summaryY + 18,
            styles: { fontSize: 8 },
            headStyles: { fillColor: [primary.r, primary.g, primary.b], textColor: 255 },
            alternateRowStyles: { fillColor: [248, 250, 252] }, // sehr helles Grau
        });

        if (includeNotes && Array.isArray(notes) && notes.length) {
            y = (doc as any).lastAutoTable?.finalY || 60;
            if (y > 260) {
                doc.addPage();
                y = 20;
            } else {
                y += 10;
            }
            doc.setFontSize(12);
            doc.text('Notizen', 14, y);
            doc.setFontSize(9);
            let yy = y + 6;
            notes.forEach((n: any, idx: number) => {
                const text = `• ${(n.text || n).toString()}`;
                doc.text(text, 16, yy);
                yy += 5;
                if (yy > 280 && idx < notes.length - 1) {
                    doc.addPage();
                    yy = 20;
                }
            });
        }

        const safeName = (title || 'Kunde').replace(/[^\w\-\u00C0-\u017F]+/g, '_');
        doc.save(`Kunde_${safeName}.pdf`);
    } catch (e) {
        console.error('Fehler beim Erzeugen des PDFs', e);
        alert('Fehler beim Erzeugen des Kunden-PDFs.');
    }
}
// Siehe templates.ts: generateWordTemplate
function generateWordDoc(type: string, c: any, u: any) {
    // Nur die wichtigsten Felder werden übergeben
    const dateStr = new Date().toLocaleDateString('de-DE');
    let s = 'Betreff', b = '', sl = `Signal Iduna - ${u.name} - Münchner Str. 209 - 85757 Karlsfeld`, r = `${c.vorname} ${c.name}<br>${c.strasse || ''}<br>${c.plz || ''} ${c.ort || ''}`, f = `<p>Mit freundlichen Grüßen</p><br><br><p>${u.name}<br>Signal Iduna</p>`;
    if (type === 'kuendigung') { sl = `${c.vorname} ${c.name} - ${c.strasse} - ${c.plz} ${c.ort}`; r = `Signal Iduna<br>Bezirksdirektion ${u.name}<br>Münchner Str. 209<br>85757 Karlsfeld`; s = 'Betreff: Kündigung meiner Versicherung'; b = `<p>Sehr geehrte Damen und Herren,</p><p>hiermit kündige ich meine Versicherung mit der Versicherungsscheinnummer: _________________________</p><p>zum nächstmöglichen Termin / zum: _________________________.</p><p>Bitte senden Sie mir eine schriftliche Kündigungsbestätigung zu.</p>`; f = `<p>Mit freundlichen Grüßen</p><br><br><p>${c.vorname} ${c.name}</p>`; }
    else if (type === 'blanko') { s = 'Betreff: Ihr Anliegen'; b = `<p>Sehr geehrte(r) ${c.name},</p><p></p><p></p>`; }
    else if (type === 'rueckruf') { s = 'Betreff: Bitte um Rückruf'; b = `<p>Sehr geehrte(r) ${c.name},</p><p>ich habe versucht, Sie telefonisch zu erreichen. Bitte rufen Sie mich zwecks Terminvereinbarung oder Besprechung Ihrer Verträge zurück.</p><p>Vielen Dank.</p>`; }
    const html = `<html xmlns:o='urn:schemas-microsoft-com:office:office' xmlns:w='urn:schemas-microsoft-com:office:word' xmlns='http://www.w3.org/TR/REC-html40'><head><meta charset="utf-8"><title>Doc</title><style>body{font-family:Arial,sans-serif;font-size:11pt;line-height:1.5}.header{font-size:8pt;text-decoration:underline;margin-bottom:30px;color:#555}.recipient-box{margin-bottom:60px;font-weight:bold}.date{text-align:right;margin-bottom:30px}.subject{font-weight:bold;margin-bottom:20px;font-size:12pt}.footer{margin-top:50px}</style></head><body><div class="header">${sl}</div><div class="recipient-box">${r}</div><div class="date">Karlsfeld, den ${dateStr}</div><div class="subject">${s}</div>${b}<div class="footer">${f}</div></body></html>`;
    const blob = new Blob(['\ufeff', html], { type: 'application/msword' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${type}_${c.name}.doc`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
}
function getDaysUntilExpiry(date: any) {
    if (!date) return 9999;
    let d: Date | null = null;
    if (typeof date === 'string' && date.includes('-')) {
        const [y, m, dStr] = date.split('-');
        d = new Date(parseInt(y), parseInt(m) - 1, parseInt(dStr));
    } else if (typeof date === 'string' && date.includes('.')) {
        const [dStr, m, y] = date.split('.');
        d = new Date(parseInt(y), parseInt(m) - 1, parseInt(dStr));
    }
    if (!d) return 9999;
    const now = new Date();
    return Math.ceil((d.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
}
function getExpiryUrgencyStyle(days: number) {
    if (days < 0) return { border: 'border-red-500', bg: 'bg-red-100', text: 'text-red-600' };
    if (days <= 30) return { border: 'border-orange-400', bg: 'bg-orange-100', text: 'text-orange-600' };
    if (days <= 90) return { border: 'border-yellow-400', bg: 'bg-yellow-100', text: 'text-yellow-600' };
    return { border: '', bg: '', text: '' };
}
function formatMoney(val: any) {
    if (typeof val === 'number') return val.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
    if (typeof val === 'string') {
        let str = val.replace(/[^\d,.-]/g, '').trim();
        if (str.includes('.') && str.includes(',') && str.lastIndexOf(',') > str.lastIndexOf('.')) {
            str = str.replace(/\./g, '').replace(',', '.');
        } else if (str.includes(',')) {
            str = str.replace(',', '.');
        }
        const num = parseFloat(str);
        if (!isNaN(num)) return num.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
    }
    return val;
}
// --- Hilfsfunktionen aus DashboardView ---
function getFirstName(n: any) {
    if (!n) return '';
    if (n.includes(',')) {
        const parts = n.split(',');
        if (parts.length > 1) return parts[1].trim().split(' ')[0];
    }
    return n.trim().split(' ')[0];
}

function checkMatch(allowed: any, c: any) {
    const normalizeNumber = (v: any) => v ? String(v).replace(/[^0-9]/g, '').replace(/^0+/, '') : '';
    const normalizeName = (n: any) => n ? n.replace(/[:.;,]/g, ' ').replace(/\s+/g, ' ').trim() : '';

    if (!allowed || !Array.isArray(allowed)) return false;
    if (allowed.some((u: any) => u.role === 'admin')) return true;
    return allowed.some((u: any) => {
        if (!u) return false;
        const uAdv = normalizeNumber(u.adv), cAdp = normalizeNumber(c.adp), cBtnr = normalizeNumber(c.btnr), uBtnr = normalizeNumber(u.btnr);
        if (uAdv.length > 2 && ((cAdp && uAdv.includes(cAdp)) || (cBtnr && uAdv.includes(cBtnr)))) return true;
        if (uBtnr.length > 2 && ((cAdp && uBtnr.includes(cAdp)) || (cBtnr && uBtnr.includes(cBtnr)))) return true;
        const uName = normalizeName(u.name).toLowerCase(), cAdpName = normalizeName(c.adp).toLowerCase();
        return uName.length > 2 && cAdpName.includes(uName);
    });
}
const allUpcomingBirthdays: any[] = [];
// displayedExpirations wird nur einmal deklariert
// const [recentCustomers, setRecentCustomers] = useState<any[]>([]); // Verschoben in die Hauptkomponente

// --- Dummy-Implementierung von syncCustomers ---
// Wird jetzt innerhalb der Komponente deklariert

// Hilfsfunktionen und Firestore-Mocks
function getColumnValue(row: any, keys: string[]): any {
    for (const key of keys) {
        if (row[key] !== undefined && row[key] !== null && row[key] !== '') return row[key];
    }
    return '';
}

function formatDateToGerman(dateStr: string): string {
    if (!dateStr || typeof dateStr !== 'string') return '';
    const trimmed = dateStr.trim();
    if (!trimmed) return '';
    // Deutsch: DD.MM.YYYY oder D.M.YYYY – nicht mit new Date() parsen (würde als US-Datum gelesen)
    if (/^\d{1,2}\.\d{1,2}\.\d{4}$/.test(trimmed)) {
        const [d, m, y] = trimmed.split('.').map(Number);
        if (d >= 1 && d <= 31 && m >= 1 && m <= 12 && y >= 1900 && y <= 2100) {
            const date = new Date(y, m - 1, d);
            if (!isNaN(date.getTime())) return date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
        }
    }
    // ISO: YYYY-MM-DD
    if (/^\d{4}-\d{2}-\d{2}/.test(trimmed)) {
        const [y, m, d] = trimmed.split('-').map(Number);
        const date = new Date(y, (m || 1) - 1, d || 1);
        if (!isNaN(date.getTime())) return date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
    }
    const d = new Date(trimmed);
    if (isNaN(d.getTime())) return dateStr;
    return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function normalizeMoney(val: any): number {
    if (typeof val === 'number') return val;
    if (typeof val === 'string') {
        let str = val.replace(/[^\d,.-]/g, '').trim();
        if (!str) return 0;
        if (str.includes('.') && str.includes(',')) {
            if (str.lastIndexOf(',') > str.lastIndexOf('.')) {
                str = str.replace(/\./g, '').replace(',', '.');
            } else {
                str = str.replace(/,/g, '');
            }
        } else if (str.includes(',')) {
            str = str.replace(',', '.');
        } else if (str.includes('.') && /\.\d{3}$/.test(str)) {
            str = str.replace(/\./g, '');
        }
        return parseFloat(str) || 0;
    }
    return 0;
}

function generateStableId(...args: any[]): string {
    return args.filter(Boolean).join('-').replace(/\s+/g, '').toLowerCase();
}

function simpleHash(str: string): string {
    let hash = 0, i, chr;
    if (str.length === 0) return hash.toString();
    for (i = 0; i < str.length; i++) {
        chr = str.charCodeAt(i);
        hash = ((hash << 5) - hash) + chr;
        hash |= 0;
    }
    return Math.abs(hash).toString(16);
}

// Papa Parse Import (falls nicht global)
import Papa from 'papaparse';
// Login/Loading States
// const [loginError, setLoginError] = useState<string | null>(null); // Verschoben in App-Komponente
// const [isLoggingIn, setIsLoggingIn] = useState(false); // Verschoben in App-Komponente
// const [loadingProgress, setLoadingProgress] = useState<number>(0); // Verschoben in App-Komponente
// Session 1 Woche speichern (PIN nur 1x pro Woche nötig)
const PERSISTED_LOGIN_KEY = 'agentur_cmr_session';
const SESSION_DAYS = 7;
const persistLogin = (user: any) => {
    try {
        const expiresAt = Date.now() + SESSION_DAYS * 24 * 60 * 60 * 1000;
        localStorage.setItem(PERSISTED_LOGIN_KEY, JSON.stringify({ userId: user.id, expiresAt }));
    } catch (e) { /* ignore */ }
};
const clearPersistedLogin = () => {
    try {
        localStorage.removeItem(PERSISTED_LOGIN_KEY);
    } catch (e) { /* ignore */ }
};
// const hasAutoImported = useRef(false); // Verschoben in App-Komponente
// Not importing lucide-react again here since it is already imported atop the file.
import { collection, onSnapshot, doc, getDoc, updateDoc, query, where, Timestamp, getFirestore, addDoc, getDocs, limit, orderBy, setDoc, writeBatch, deleteField, deleteDoc } from 'firebase/firestore';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { useTheme } from './contexts/ThemeContext';
import { calculateDuration } from './components/views/RosterView';
import { initializeApp } from 'firebase/app';
import { getAuth, signInWithCustomToken, signInAnonymously, onAuthStateChanged } from 'firebase/auth';

// Typen für AgencyData und Inputs
type RowType = {
    category: string;
    basis: string;
    label: string;
    rate: number;
    unit: string;
    factor: number;
};
type AgencyDataType = {
    [key: string]: RowType[];
};
type InputsType = {
    [key: string]: string;
};
declare global {
    interface Window {
        XLSX: any;
    }
}
const DEMO_DATA = {
    "Bezirksdirektion": [
        { category: "Kranken", basis: "Monatsbeitrag", label: "Neu", rate: 6.5, unit: "MB", factor: 1.0 },
        { category: "Leben", basis: "Beitragssumme", label: "Neu Comfort", rate: 35.0, unit: "‰", factor: 0.005 },
        { category: "Leben", basis: "Beitragssumme", label: "Neu Classic", rate: 17.5, unit: "‰", factor: 0.0025 },
        { category: "Risikounfall", basis: "Monatsbeitrag", label: "Neu mit Dyn.", rate: 81.25, unit: "%", factor: 0.1125 },
        { category: "Risikounfall", basis: "Monatsbeitrag", label: "Neu ohne Dyn.", rate: 65.0, unit: "%", factor: 0.09 },
        { category: "Risikounfall", basis: "Monatsbeitrag", label: "Verl. mit Dyn.", rate: 28.44, unit: "%", factor: 0.0394 },
        { category: "Vita Life", basis: "Monatsbeitrag", label: "Neu mit Dyn.", rate: 81.25, unit: "%", factor: 0.0563 },
        { category: "Vita Life", basis: "Monatsbeitrag", label: "Neu ohne Dyn.", rate: 65.0, unit: "%", factor: 0.045 },
    ],
    "Generalagentur 1": [
        { category: "Kranken", basis: "Monatsbeitrag", label: "Neu", rate: 5.5, unit: "MB", factor: 1.0 },
        { category: "Leben", basis: "Beitragssumme", label: "Neu Comfort", rate: 30.0, unit: "‰", factor: 0.005 },
        { category: "Leben", basis: "Beitragssumme", label: "Neu Classic", rate: 15.0, unit: "‰", factor: 0.0025 },
        { category: "Risikounfall", basis: "Monatsbeitrag", label: "Neu mit Dyn.", rate: 68.75, unit: "%", factor: 0.1125 },
        { category: "Risikounfall", basis: "Monatsbeitrag", label: "Neu ohne Dyn.", rate: 55.0, unit: "%", factor: 0.09 },
    ],
    "Generalagentur 2": [
        { category: "Kranken", basis: "Monatsbeitrag", label: "Neu", rate: 6.0, unit: "MB", factor: 1.0 },
        { category: "Leben", basis: "Beitragssumme", label: "Neu Comfort", rate: 32.0, unit: "‰", factor: 0.005 },
        { category: "Leben", basis: "Beitragssumme", label: "Neu Classic", rate: 16.0, unit: "‰", factor: 0.0025 },
        { category: "Risikounfall", basis: "Monatsbeitrag", label: "Neu mit Dyn.", rate: 75.0, unit: "%", factor: 0.1125 },
    ],
    "Hauptagentur": [
        { category: "Kranken", basis: "Monatsbeitrag", label: "Neu", rate: 5.0, unit: "MB", factor: 1.0 },
        { category: "Leben", basis: "Beitragssumme", label: "Neu Comfort", rate: 28.0, unit: "‰", factor: 0.005 },
        { category: "Leben", basis: "Beitragssumme", label: "Neu Classic", rate: 14.0, unit: "‰", factor: 0.0025 },
    ],
    "Agentur": [
        { category: "Kranken", basis: "Monatsbeitrag", label: "Neu", rate: 4.5, unit: "MB", factor: 1.0 },
        { category: "Leben", basis: "Beitragssumme", label: "Neu Comfort", rate: 26.0, unit: "‰", factor: 0.005 },
    ]
};

function App() {
    const authContext = useAuth();
    // Dummy-States für Import/Sync-Status, damit die App nicht crasht
    const [syncStatus, setSyncStatus] = useState<string>('idle');
    const [isImporting, setIsImporting] = useState<boolean>(false);
    const [importError, setImportError] = useState<any>(null);
    const [lastSynced, setLastSynced] = useState<number | null>(null);
    const [excelImportResult, setExcelImportResult] = useState<any>(null);
    const [isUploadingExcel, setIsUploadingExcel] = useState<boolean>(false);
    const syncInProgressRef = useRef(false);

    // syncCustomers: Lädt Kunden aus Backend API
    async function syncCustomers(mode: "login" | "background") {
        try {
            const count = await fetchKundenlisteData(mode === "background");
            return { ok: true, count };
        } catch (e) {
            console.warn('[syncCustomers] Fehler:', e);
            return { ok: false };
        }
    }
    const hasAutoImported = useRef(false);
    const [loginError, setLoginError] = useState<string | null>(null);
    const [isLoggingIn, setIsLoggingIn] = useState(false);
    const [loadingProgress, setLoadingProgress] = useState<number>(0);
    const [toast, setToast] = useState<{ message: string; type?: string } | null>(null);
    const [confirmDialog, setConfirmDialog] = useState<ConfirmDialogConfig | null>(null);
    const [recentCustomers, setRecentCustomers] = useState<any[]>([]);
    const [rawCustomers, setRawCustomers] = useState<Customer[]>([]);
    const [activeLevel, setActiveLevel] = useState<string>("Bezirksdirektion");
    const [currentView, setCurrentView] = useState<string>('dashboard');
    // (Doppelte useState und useRef Initialisierungen entfernt, stehen jetzt nur noch am Anfang der Komponente)
    const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false); // Standard: nicht eingeloggt
    const [pdfNotesCustomer, setPdfNotesCustomer] = useState<any>(null);
    const [pdfNotesData, setPdfNotesData] = useState<any[]>([]);
    const [goalFormData, setGoalFormData] = useState<any>({});
    const [permissionsTargetUser, setPermissionsTargetUser] = useState<any>(null);
    const [editingUserAllowedToView, setEditingUserAllowedToView] = useState<any[]>([]);
    const [userFormData, setUserFormData] = useState<any>({});
    const [expTab, setExpTab] = useState<'open' | 'done'>('open');
    const [expDateFrom, setExpDateFrom] = useState('');
    const [expDateTo, setExpDateTo] = useState('');
    const [expSparteFilter, setExpSparteFilter] = useState('');
    const [adminView, setAdminView] = useState<'admin' | 'employee'>('employee');
    const [firebaseLoadTrigger, setFirebaseLoadTrigger] = useState(0);
    const { isDark, toggleTheme } = useTheme();
    const [newTaskDefaults, setNewTaskDefaults] = useState<any>({});
    const [agencyData, setAgencyData] = useState<AgencyDataType>(DEMO_DATA);
    const [inputs, setInputs] = useState<InputsType>({});
    const [libLoaded, setLibLoaded] = useState(false);
    const [isDragging, setIsDragging] = useState(false);
    const [error, setError] = useState('');
    const [successMsg, setSuccessMsg] = useState('');
    const [closings, setClosings] = useState<any[]>([]);
    const [passwords, setPasswords] = useState<any[]>([]);
    const [notesMap, setNotesMap] = useState<any>({});
    const [contractStates, setContractStates] = useState<any>({});
    const [logs, setLogs] = useState<any[]>([]);
    const [provisionRulesFromFirebase, setProvisionRulesFromFirebase] = useState<any>(null);
    const [dashboardWidgetOrder, setDashboardWidgetOrder] = useState<string[] | null>(null);
    const [goals, setGoals] = useState<any[]>([]);
    const [letterTemplates, setLetterTemplates] = useState<any[]>([]);
    const [selectedCustomersForLetters, setSelectedCustomersForLetters] = useState<any[]>([]);
    const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');
    const [isGeneratingLetters, setIsGeneratingLetters] = useState(false);
    const [pdfGenerationProgress, setPdfGenerationProgress] = useState(0);
    const [previewCustomer, setPreviewCustomer] = useState<any>(null);
    const [showPdfNotesDialog, setShowPdfNotesDialog] = useState(false);
    const [showTaskModal, setShowTaskModal] = useState(false);
    const [taskFormTitle, setTaskFormTitle] = useState('');
    const [taskFormDescription, setTaskFormDescription] = useState('');
    const [taskFormAssignedTo, setTaskFormAssignedTo] = useState('');
    const [showTasksListModal, setShowTasksListModal] = useState(false);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [users, setUsers] = useState<AppUser[]>([]);
    const [firebaseUser, setFirebaseUser] = useState<unknown>(null);
    const [currentUser, setCurrentUser] = useState<AppUser | null>(null);
    const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);

    // Modal-States (müssen vor der ersten Verwendung stehen)
    const [showGoalModal, setShowGoalModal] = useState(false);
    const [showPermissionsModal, setShowPermissionsModal] = useState(false);
    const [showBirthdayModal, setShowBirthdayModal] = useState(false);
    const [showCustomerDetailModal, setShowCustomerDetailModal] = useState(false);
    const [showExpirationModal, setShowExpirationModal] = useState(false);
    const [showPdfPreview, setShowPdfPreview] = useState(false);
    const [showLetterGenerator, setShowLetterGenerator] = useState(false);
    const [showUserModal, setShowUserModal] = useState(false);
    const [showPwdModal, setShowPwdModal] = useState(false);
    const [searchOpen, setSearchOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [isMobile, setIsMobile] = useState(false);

    // Aufgaben, Dienstplan und Urlaube (müssen vor der ersten Verwendung stehen)
    const [tasks, setTasks] = useState<any[]>([]);
    const [shifts, setShifts] = useState<any[]>([]);
    const [vacations, setVacations] = useState<any[]>([]);

    // Task-Modal: Formular beim Öffnen aus newTaskDefaults/currentUser füllen
    useEffect(() => {
        if (showTaskModal) {
            setTaskFormTitle(newTaskDefaults.title ?? '');
            setTaskFormDescription(newTaskDefaults.description ?? '');
            setTaskFormAssignedTo(currentUser?.name ?? (users[0]?.name) ?? 'Alle');
        }
    }, [showTaskModal]);

    // Firebase/Firestore Setup
    const firebaseConfig = {
        apiKey: "AIzaSyAtuk5zW_Q4K65ANwuANPypwmmvSOQfKlA",
        authDomain: "agenturmanager-96ff4.firebaseapp.com",
        projectId: "agenturmanager-96ff4",
        storageBucket: "agenturmanager-96ff4.appspot.com",
        messagingSenderId: "510352845810",
        appId: "1:510352845810:web:5ad373711c092465d3996b"
    };
    const app = initializeApp(firebaseConfig);
    const db = getFirestore(app);
    const firebaseAuth = getAuth(app);
    const APP_ID = 'agenturmanager-crm';
    // Zuerst agenturmanager-crm (Daten unter artifacts/agenturmanager-crm/public/data/users), dann 96ff4
    const APP_IDS_TO_TRY = ['agenturmanager-crm', 'agenturmanager-96ff4'];

    // Firestore: welcher Pfad genutzt wird; discoveredAppId = gefundene App-ID unter artifacts
    const firestoreBasePath = useRef<'app' | 'root'>('app');
    const discoveredAppId = useRef<string>(APP_IDS_TO_TRY[0]);
    // Wenn Nutzer aus Root-Collection geladen wurden (Fallback), Schreibvorgänge auch in Root
    const usersFromRootRef = useRef<boolean>(false);

    // Firebase: Anonym anmelden, dann Firestore-Daten laden (beide App-Pfade → Root → weitere artifacts)
    useEffect(() => {
        const rootColl = (name: string) => collection(db, name);
        const appCollFor = (appId: string) => (name: string) =>
            collection(doc(db, 'artifacts', appId, 'public', 'data'), name);
        const dataRefFor = (appId: string) => doc(db, 'artifacts', appId, 'public', 'data');

        const applySnapshots = (r: {
            usersSnap: any; customersSnap: any; tasksSnap: any; shiftsSnap: any; goalsSnap: any;
            templatesSnap: any; vacationsSnap: any; notesSnap: any; closingsSnap: any; contractStatesSnap: any;
            logsSnap?: any; provisionRulesSnap?: any; passwordsSnap?: any;
        }) => {
            setUsers(r.usersSnap.docs.map((d: any) => ({ id: d.id, ...d.data() })));
            // Kundenliste kommt aus Backend API / Cache
            setTasks(r.tasksSnap.docs.map((d: any) => ({ id: d.id, ...d.data() })));
            setShifts(r.shiftsSnap.docs.map((d: any) => ({ id: d.id, ...d.data() })));
            setGoals(r.goalsSnap.docs.map((d: any) => ({ id: d.id, ...d.data() })));
            setLetterTemplates(r.templatesSnap.docs.map((d: any) => ({ id: d.id, ...d.data() })));
            setVacations(r.vacationsSnap.docs.map((d: any) => ({ id: d.id, ...d.data() })));
            if (r.passwordsSnap) {
                setPasswords(r.passwordsSnap.docs.map((d: any) => ({ id: d.id, ...d.data() })));
            }
            const notes: any = {};
            r.notesSnap.docs.forEach((d: any) => { notes[d.id] = (d.data().notes || []); });
            if (Object.keys(notes).length > 0) setNotesMap(notes);
            const closingsData = (r.closingsSnap as any)?.exists ? (r.closingsSnap as any).data() : null;
            if (closingsData?.dates && Array.isArray(closingsData.dates)) setClosings(closingsData.dates);
            const states: any = {};
            r.contractStatesSnap.docs.forEach((d: any) => { states[d.id] = d.data().status || 'open'; });
            if (Object.keys(states).length > 0) setContractStates(states);
            if (r.logsSnap?.docs?.length) setLogs(r.logsSnap.docs.map((d: any) => ({ id: d.id, ...d.data() })));
            if (r.provisionRulesSnap?.exists?.()) {
                const data = r.provisionRulesSnap.data();
                if (data && typeof data === 'object' && Array.isArray(data.AGT)) setProvisionRulesFromFirebase(data);
            } else setProvisionRulesFromFirebase(null);
        };

        const emptyCustomersSnap = { docs: [] as any[], size: 0 };
        const loadFromAppPath = async (appId: string) => {
            const dataRef = dataRefFor(appId);
            const appColl = appCollFor(appId);
            const [usersSnap, tasksSnap, shiftsSnap, goalsSnap, templatesSnap, vacationsSnap, notesSnap, closingsSnap, contractStatesSnap, logsSnap, provisionRulesSnap, passwordsSnap] = await Promise.all([
                getDocs(appColl('users')),
                getDocs(appColl('tasks')),
                getDocs(appColl('roster_shifts')),
                getDocs(appColl('goals')),
                getDocs(appColl('letter_templates')),
                getDocs(appColl('vacations')),
                getDocs(appColl('customer_notes')),
                getDoc(doc(collection(dataRef, 'roster_closings'), 'list')),
                getDocs(appColl('contract_states')),
                getDocs(appColl('logs')).catch(() => ({ docs: [] })),
                getDoc(doc(collection(dataRef, 'provision_rules'), 'data')).catch(() => ({ exists: () => false })),
                getDocs(appColl('passwords')).catch(() => ({ docs: [] }))
            ]);
            const total = usersSnap.size + tasksSnap.size + shiftsSnap.size + goalsSnap.size;
            return { total, usersSnap, customersSnap: emptyCustomersSnap, tasksSnap, shiftsSnap, goalsSnap, templatesSnap, vacationsSnap, notesSnap, closingsSnap, contractStatesSnap, logsSnap, provisionRulesSnap, passwordsSnap };
        };

        const runLoad = async () => {
            try {
                console.log('[Firebase] Projekt:', firebaseConfig.projectId, '– Suche nach Daten...');
                // 1) Bekannte App-Pfade nacheinander: zuerst agenturmanager-crm (Daten-Pfad), dann 96ff4
                for (const appId of APP_IDS_TO_TRY) {
                    console.log('[Firebase] Prüfe artifacts/' + appId + '/public/data...');
                    const result = await loadFromAppPath(appId);
                    if (result.total > 0) {
                        firestoreBasePath.current = 'app';
                        discoveredAppId.current = appId;
                        // Fallback: Wenn unter App-Pfad keine Nutzer, Nutzer aus Root laden (häufig liegen Mitarbeiter in Root)
                        let usersSnap = result.usersSnap;
                        usersFromRootRef.current = false;
                        if (result.usersSnap.size === 0) {
                            const rootUsersSnap = await getDocs(rootColl('users'));
                            if (rootUsersSnap.size > 0) {
                                usersSnap = rootUsersSnap;
                                usersFromRootRef.current = true;
                                console.log('[Firebase] Nutzer aus Root-Collection geladen:', rootUsersSnap.size);
                            }
                        }
                        applySnapshots({ ...result, usersSnap });
                        console.log('[Firebase]', usersSnap.size, 'Benutzer,', result.tasksSnap.size, 'Aufgaben,', result.shiftsSnap.size, 'Schichten,', result.goalsSnap.size, 'Ziele geladen (Pfad: artifacts/' + appId + '/public/data)');
                        return;
                    }
                }
                // 2) Root-Collections
                console.log('[Firebase] App-Pfad leer – versuche Root-Collections...');
                const [ru, rt, rs, rg] = await Promise.all([
                    getDocs(rootColl('users')),
                    getDocs(rootColl('tasks')),
                    getDocs(rootColl('roster_shifts')),
                    getDocs(rootColl('goals'))
                ]);
                const rootTotal = ru.size + rt.size + rs.size + rg.size;
                if (rootTotal > 0) {
                    firestoreBasePath.current = 'root';
                    usersFromRootRef.current = true;
                    const [rTemplates, rVacations, rNotes, rClosingsSnap, rStates, rLogs, rProvisionRules, rPasswords] = await Promise.all([
                        getDocs(rootColl('letter_templates')).catch(() => ({ docs: [] })),
                        getDocs(rootColl('vacations')).catch(() => ({ docs: [] })),
                        getDocs(rootColl('customer_notes')).catch(() => ({ docs: [] })),
                        getDoc(doc(db, 'roster_closings', 'list')).catch(() => ({ exists: () => false })),
                        getDocs(rootColl('contract_states')).catch(() => ({ docs: [] })),
                        getDocs(rootColl('logs')).catch(() => ({ docs: [] })),
                        getDoc(doc(db, 'provision_rules', 'data')).catch(() => ({ exists: () => false })),
                        getDocs(rootColl('passwords')).catch(() => ({ docs: [] }))
                    ]).then(arr => arr as any);
                    applySnapshots({
                        usersSnap: ru, customersSnap: emptyCustomersSnap, tasksSnap: rt, shiftsSnap: rs, goalsSnap: rg,
                        templatesSnap: rTemplates, vacationsSnap: rVacations, notesSnap: rNotes,
                        closingsSnap: rClosingsSnap, contractStatesSnap: rStates, logsSnap: rLogs,
                        provisionRulesSnap: rProvisionRules, passwordsSnap: rPasswords
                    });
                    console.log('[Firebase]', ru.size, 'Benutzer,', rt.size, 'Aufgaben,', rs.size, 'Schichten,', rg.size, 'Ziele (Root-Collections)');
                    return;
                }
                // 3) Alle anderen artifacts durchsuchen (App-IDs, die noch nicht in APP_IDS_TO_TRY waren)
                console.log('[Firebase] Root leer – durchsuche weitere artifacts/.../public/data...');
                const artifactsSnap = await getDocs(collection(db, 'artifacts'));
                const appIds = artifactsSnap.docs.map((d: any) => d.id).filter((id: string) => !APP_IDS_TO_TRY.includes(id));
                for (const appId of appIds) {
                    try {
                        const res = await loadFromAppPath(appId);
                        if (res.total > 0) {
                            firestoreBasePath.current = 'app';
                            discoveredAppId.current = appId;
                            let usersSnap = res.usersSnap;
                            usersFromRootRef.current = false;
                            if (res.usersSnap.size === 0) {
                                const rootUsersSnap = await getDocs(rootColl('users'));
                                if (rootUsersSnap.size > 0) {
                                    usersSnap = rootUsersSnap;
                                    usersFromRootRef.current = true;
                                }
                            }
                            applySnapshots({ ...res, usersSnap });
                            console.log('[Firebase]', usersSnap.size, 'Benutzer,', res.tasksSnap.size, 'Aufgaben,', res.shiftsSnap.size, 'Schichten,', res.goalsSnap.size, 'Ziele (App-ID: ' + appId + ')');
                            return;
                        }
                    } catch (_) { }
                }
                console.log('[Firebase] Keine Daten gefunden (' + APP_ID + ', Root und', appIds.length, 'weitere App-IDs unter artifacts).');
                console.log('[Firebase] Bitte in der Firebase Console prüfen: https://console.firebase.google.com/ → Projekt', firebaseConfig.projectId, '→ Firestore → Data. Unter welcher Collection/Pfad liegen die Daten?');
            } catch (e: any) {
                const msg = e?.message || String(e);
                console.error('[Firebase] Firestore-Laden fehlgeschlagen:', e);
            }
        };

        const unsub = onAuthStateChanged(firebaseAuth, (u) => {
            setFirebaseUser(u);
            if (u) {
                // Kundenliste aus Cache – sofort anzeigen
                try {
                    let cached = localStorage.getItem('crm_customers_cache') || localStorage.getItem('crm_data_cache');
                    if (cached) {
                        const arr = JSON.parse(cached);
                        if (Array.isArray(arr) && arr.length > 0) setRawCustomers(arr);
                    }
                } catch (e) { }
                runLoad();
            }
        });

        signInAnonymously(firebaseAuth).catch((err: any) => {
            console.warn('[Firebase] Anonym-Anmeldung:', err?.message || err);
        });

        return () => unsub();
    }, [firebaseAuth, firebaseLoadTrigger]);

    // Firestore-Pfad-Helfer (nutzt discoveredAppId wenn Daten unter anderer App-ID gefunden)
    const getFirestoreColl = (name: string) => {
        if (name === 'users' && usersFromRootRef.current)
            return collection(db, 'users');
        return firestoreBasePath.current === 'root'
            ? collection(db, name)
            : collection(doc(db, 'artifacts', discoveredAppId.current, 'public', 'data'), name);
    };
    const getFirestoreDoc = (collName: string, docId: string) => {
        if (collName === 'users' && usersFromRootRef.current)
            return doc(db, 'users', docId);
        return firestoreBasePath.current === 'root'
            ? doc(db, collName, docId)
            : doc(db, 'artifacts', discoveredAppId.current, 'public', 'data', collName, docId);
    };

    const addLog = useCallback(async (action: string, message: string, type?: string, id?: any, metadata?: { before?: any; after?: any }) => {
        if (!firebaseUser || !db) return;
        try {
            const payload: any = { action, message, type: type || '', id: id ?? null, createdAt: new Date().toISOString(), user: currentUser?.name || 'System' };
            if (metadata && (metadata.before !== undefined || metadata.after !== undefined)) payload.metadata = metadata;
            const ref = await addDoc(getFirestoreColl('logs'), payload);
            setLogs(prev => [...prev, { id: ref.id, ...payload }]);
        } catch (e: any) {
            console.warn('[addLog] Firestore write failed:', e?.message);
        }
    }, [firebaseUser, db, currentUser?.name]);

    const saveProvisionRules = async (data: any) => {
        if (!firebaseUser || !db) return;
        const ref = getFirestoreDoc('provision_rules', 'data');
        await setDoc(ref, data);
        setProvisionRulesFromFirebase(data);
        addLog('Provisionssätze gespeichert', 'Alle Agenturstufen', 'settings', 'provision_rules');
    };

    const loadBoniTables = useCallback(async (): Promise<any> => {
        if (!db) return null;
        try {
            const ref = getFirestoreDoc('boni_tables', 'data');
            const snap = await getDoc(ref);
            if (snap.exists() && snap.data()?.data) return snap.data().data;
        } catch (e) { console.warn('[loadBoniTables]', e); }
        return null;
    }, [db]);

    const saveBoniTables = useCallback(async (data: any) => {
        if (!firebaseUser || !db) return;
        try {
            await setDoc(getFirestoreDoc('boni_tables', 'data'), {
                data,
                updatedAt: new Date().toISOString(),
                updatedBy: currentUser?.name || 'System'
            });
            showToast('Boni-Tabellen gespeichert.');
            addLog('Boni-Tabellen gespeichert', 'Bonitafeln aktualisiert', 'settings', 'boni_tables');
        } catch (e: any) {
            console.error('[saveBoniTables]', e);
            showToast('Fehler beim Speichern der Boni-Tabellen', 'error');
        }
    }, [firebaseUser, db, currentUser?.name]);

    // Nach Login erneut laden (falls Trigger; nutzt dieselbe App-ID wie beim ersten Laden)
    useEffect(() => {
        if (!db || !firebaseUser || !isAuthenticated) return;
        if (firebaseLoadTrigger === 0) return;
        const dataRef = doc(db, 'artifacts', discoveredAppId.current, 'public', 'data');
        const coll = (name: string) => collection(dataRef, name);
        const runLoad = async () => {
            try {
                const [usersSnap, tasksSnap, shiftsSnap, goalsSnap, vacationsSnap, passwordsSnap] = await Promise.all([
                    getDocs(coll('users')),
                    getDocs(coll('tasks')),
                    getDocs(coll('roster_shifts')),
                    getDocs(coll('goals')),
                    getDocs(coll('vacations')),
                    getDocs(coll('passwords')).catch(() => ({ docs: [] }))
                ]);
                setUsers(usersSnap.docs.map((d: any) => ({ id: d.id, ...d.data() })));
                setTasks(tasksSnap.docs.map((d: any) => ({ id: d.id, ...d.data() })));
                setShifts(shiftsSnap.docs.map((d: any) => ({ id: d.id, ...d.data() })));
                setGoals(goalsSnap.docs.map((d: any) => ({ id: d.id, ...d.data() })));
                setVacations(vacationsSnap.docs.map((d: any) => ({ id: d.id, ...d.data() })));
                setPasswords(passwordsSnap.docs.map((d: any) => ({ id: d.id, ...d.data() })));
            } catch (e: any) {
                console.error('[Firebase]', e?.message || e);
            }
        };
        runLoad();
    }, [db, firebaseUser, isAuthenticated, firebaseLoadTrigger]);

    // Hilfsfunktionen – Toast deaktiviert (soll nie mehr erscheinen)
    const showToast = (_message: string, _type?: string) => { };

    const requestConfirm = useCallback((message: string, onConfirm: () => void, options?: { variant?: 'danger'; title?: string; confirmText?: string }) => {
        setConfirmDialog({ message, onConfirm, variant: options?.variant ?? 'default', title: options?.title, confirmText: options?.confirmText });
    }, []);

    // ...alle weiteren Hilfsfunktionen und Logik...

    // Seitentitel im Tab je nach View (Punkt 19)
    const viewLabelMap: Record<string, string> = {
        dashboard: 'Übersicht', customers: 'Kunden', bestand: 'Bestand', roster: 'Dienstplan', goals: 'Ziele',
        provisionen: 'Provisionen', boni: 'Boni-Rechner', tasks: 'Aufgaben', expirations: 'Abläufe', verlaengerung: 'Verlängerung',
        birthdays: 'Geburtstage', phonebook: 'Telefonbuch', passwords: 'Passwörter', users: 'Team',
        settings: 'Einstellungen', activity: 'Protokoll', stats: 'Statistik', templates: 'Vorlagen', boni_tables: 'Boni-Tabellen', admin: 'Import',
    };
    useEffect(() => {
        const base = 'AgenturCRM';
        const viewLabel = viewLabelMap[currentView] || currentView;
        const title = selectedCustomer
            ? `${selectedCustomer.vorname || ''} ${selectedCustomer.name || ''}`.trim() + ` – ${viewLabel} – ${base}`
            : `${viewLabel} – ${base}`;
        document.title = title;
    }, [currentView, selectedCustomer]);

    // Dashboard-Widget-Reihenfolge aus Firestore (Punkt 8)
    useEffect(() => {
        if (!db || !currentUser?.id) return;
        getDoc(getFirestoreDoc('user_preferences', currentUser.id))
            .then((snap: any) => {
                if (snap.exists() && Array.isArray(snap.data()?.dashboardWidgetOrder))
                    setDashboardWidgetOrder(snap.data().dashboardWidgetOrder);
            })
            .catch(() => { });
    }, [db, currentUser?.id]);

    const saveDashboardPreferences = useCallback(async (order: string[]) => {
        if (!firebaseUser || !db || !currentUser?.id) return;
        try {
            await setDoc(getFirestoreDoc('user_preferences', currentUser.id), {
                dashboardWidgetOrder: order,
                updatedAt: new Date().toISOString()
            });
            setDashboardWidgetOrder(order);
            showToast('Dashboard angepasst.');
        } catch (e: any) {
            showToast('Fehler beim Speichern', 'error');
        }
    }, [firebaseUser, db, currentUser?.id]);

    // Keyboard: Strg+K Suche, Esc schließt Modals (Punkt 3)
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                setSearchOpen(false);
                if (confirmDialog) setConfirmDialog(null);
            }
            if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
                e.preventDefault();
                setSearchOpen(true);
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [confirmDialog]);

    // DEBUG: Render-Start


    // --- Login/Authentifizierung ---
    const handleLogin = async (code: any) => {
        // Admin code always works immediately
        if (code === '8888') {
            const user: AppUser = { id: 'admin', name: 'Admin', role: 'admin', code: '8888', level: '' } as AppUser;
            setCurrentUser(user);
            // AuthProvider korrekt setzen
            authContext.setUser(user);
            console.log('[handleLogin] setUser für Admin:', user);
            setAdminView('admin'); // Admin-Bereich (Import, Protokoll, Vorlagen) anzeigen
            setLoginError("");
            setLoadingProgress(0);
            setIsLoggingIn(true);
            setLoadingProgress(10);
            // Load Cache
            let cachedExists = false;
            let cacheAge = Infinity;
            try {
                const cached = localStorage.getItem('crm_data_cache');
                const cachedTime = localStorage.getItem('crm_data_cache_time');
                if (cached) {
                    setRawCustomers(JSON.parse(cached));
                    cachedExists = true;
                    if (cachedTime) {
                        cacheAge = Date.now() - parseInt(cachedTime);
                    }
                }
            } catch (e) { }
            setLoadingProgress(50);
            // Wenn Cache frisch ist (< 10 Min), direkt einloggen und Sync im Hintergrund
            const tenMinutes = 10 * 60 * 1000;
            if (cachedExists && cacheAge < tenMinutes) {
                setLoadingProgress(100);
                setIsAuthenticated(true);
                setIsLoggingIn(false);
                setCurrentView('dashboard');
                setSelectedCustomer(null);
                persistLogin(user);
                // Background sync
                setTimeout(() => {
                    syncCustomers("background").catch(console.error);
                }, 2000);
                return;
            }
            // Sync im Hintergrund versuchen, Einloggen nicht davon abhängig machen (Cache ist schon geladen)
            setLoadingProgress(100);
            setIsAuthenticated(true);
            setIsLoggingIn(false);
            setCurrentView('dashboard');
            setSelectedCustomer(null);
            persistLogin(user);
            setTimeout(() => addLog('Login', 'Admin hat sich eingeloggt'), 100);
            syncCustomers("login").catch(() => { });
            return;
        }
        // User-Login: Code mit Nutzern aus Firebase abgleichen
        setLoginError("");
        setLoadingProgress(0);
        setIsLoggingIn(true);
        const codeStr = String(code).trim();
        const match = users.find((u: any) => u && String(u.code || '').trim() === codeStr);
        if (!match) {
            setIsLoggingIn(false);
            if (users.length === 0) {
                setLoginError("Daten werden noch geladen. Bitte kurz warten und erneut versuchen.");
            } else {
                setLoginError("Ungültiger PIN oder Benutzer nicht gefunden.");
            }
            return;
        }
        // Sofort Cache anzeigen (falls vorhanden), dann Liste im Hintergrund laden
        try {
            const cached = localStorage.getItem('crm_data_cache') || localStorage.getItem('crm_customers_cache');
            if (cached) {
                const arr = JSON.parse(cached);
                if (Array.isArray(arr) && arr.length > 0) setRawCustomers(arr);
            }
        } catch (e) { }
        setLoadingProgress(30);
        try {
            const count = await fetchKundenlisteData(false);
            if (count > 0) {
                setLoadingProgress(100);
                setCurrentUser(match);
                setAdminView(match.role === 'admin' ? 'admin' : 'employee');
                setIsAuthenticated(true);
                setIsLoggingIn(false);
                setCurrentView('dashboard');
                setSelectedCustomer(null);
                persistLogin(match);
                setTimeout(() => addLog('Login', `${match.name} hat sich eingeloggt`), 100);
                return;
            }
        } catch (e) {
            console.warn('[Login] Kundenliste nicht geladen, nutze Cache:', e);
        }
        setLoadingProgress(100);
        setCurrentUser(match);
        setAdminView(match.role === 'admin' ? 'admin' : 'employee');
        setIsAuthenticated(true);
        setIsLoggingIn(false);
        setCurrentView('dashboard');
        setSelectedCustomer(null);
        persistLogin(match);
        setTimeout(() => addLog('Login', `${match.name} hat sich eingeloggt`), 100);
    };

    const handleLogout = () => {
        clearPersistedLogin();
        setCurrentUser(null);
        setIsAuthenticated(false);
        setLoginError(null);
        setAdminView('employee');
        setCurrentView('dashboard');
        setSelectedCustomer(null);
    };

    // Beim Start: gespeicherte Session (1 Woche) wiederherstellen, wenn gültig
    const persistedLoginRestored = useRef(false);
    useEffect(() => {
        if (isAuthenticated || users.length === 0 || persistedLoginRestored.current) return;
        try {
            const raw = localStorage.getItem(PERSISTED_LOGIN_KEY);
            if (!raw) return;
            const data = JSON.parse(raw);
            const { userId, expiresAt } = data || {};
            if (!userId || !expiresAt || expiresAt < Date.now()) {
                clearPersistedLogin();
                return;
            }
            const match = users.find((u: any) => u && u.id === userId);
            if (!match) return;
            persistedLoginRestored.current = true;
            setCurrentUser(match);
            setAdminView(match.role === 'admin' ? 'admin' : 'employee');
            setIsAuthenticated(true);
            setCurrentView('dashboard');
            setSelectedCustomer(null);
        } catch (e) {
            clearPersistedLogin();
        }
    }, [users, isAuthenticated]);

    const bgSyncAttempted = useRef(false);
    useEffect(() => {
        if (isAuthenticated && !bgSyncAttempted.current) {
            bgSyncAttempted.current = true;
            const lastSync = localStorage.getItem('crm_last_sync');
            const now = Date.now();
            if (!lastSync || (now - parseInt(lastSync) > 10 * 60 * 1000)) {
                syncCustomers("background").catch(console.error);
            }
        }
    }, [isAuthenticated]);


    // Lädt Kunden aus Backend API
    const fetchKundenlisteData = (isAuto = false): Promise<number> => {
        return new Promise((resolve, reject) => {
            if (syncInProgressRef.current || syncStatus === 'syncing') {
                if (!isAuto) {
                    console.warn('[syncCustomers] Blockiert: Sync bereits aktiv! isAuto:', isAuto, 'syncStatus:', syncStatus);
                }
                if (!isAuto) showToast('Synchronisation läuft bereits.', 'info');
                resolve(0);
                return;
            }


            syncInProgressRef.current = true;
            setIsImporting(true);
            setImportError(null);
            setSyncStatus('syncing');

            // Backend-URL und API-Secret aus Environment-Variablen
            let CUSTOMERS_API_URL = `${window.location.protocol}//${window.location.hostname}:5001`; // Port 5001 ist laut server/index.ts der Backend-Port
            let CUSTOMERS_API_SECRET = '';

            if (typeof import.meta !== 'undefined' && import.meta.env) {
                const env = import.meta.env;

                // Bevorzugte URL aus .env (VITE_VERLAENGERUNG_BACKEND_URL oder VITE_API_URL)
                const envUrl = env.VITE_VERLAENGERUNG_BACKEND_URL || env.VITE_API_URL;

                if (envUrl && !env.DEV) {
                    CUSTOMERS_API_URL = envUrl;
                } else if (env.DEV) {
                    CUSTOMERS_API_URL = '';
                }

                if (env.VITE_VERLAENGERUNG_API_SECRET) {
                    CUSTOMERS_API_SECRET = env.VITE_VERLAENGERUNG_API_SECRET;
                }
            }

            if (CUSTOMERS_API_URL == null || !CUSTOMERS_API_SECRET) {
                syncInProgressRef.current = false;
                setIsImporting(false);
                setSyncStatus('error');
                setImportError('Backend nicht konfiguriert. Bitte .env prüfen.');
                if (!isAuto) showToast('Backend nicht konfiguriert. Bitte .env prüfen.', 'error');
                reject(new Error('Backend nicht konfiguriert'));
                return;
            }
            
            // Timeout: Setze syncStatus nach 60 Sekunden auf 'error', falls nicht abgeschlossen
            const syncTimeout = setTimeout(() => {
                syncInProgressRef.current = false;
                setSyncStatus('error');
                setIsImporting(false);
                setImportError('Synchronisation abgebrochen (Timeout).');
            }, 60000);

            // Backend API aufrufen
            let customersUrl = `${CUSTOMERS_API_URL}/api/customers`;
            const separator = customersUrl.includes('?') ? '&' : '?';
            customersUrl = `${customersUrl}${separator}apiSecret=${encodeURIComponent(CUSTOMERS_API_SECRET)}`;

            fetch(customersUrl)
                .then((res) => {
                    if (!res.ok) {
                        throw new Error(`HTTP ${res.status}: ${res.statusText}`);
                    }
                    return res.json();
                })
                .then((data) => {
                    const customers = data.customers || [];
                    const normalized = customers.map((c: any) => ({
                        ...c,
                        verträge: c.verträge || c.vertraege || [],
                        vertraege: c.vertraege || c.verträge || []
                    }));

                    setRawCustomers(normalized);

                    // Cache löschen, wenn Backend leer ist (0 Kunden)
                    if (normalized.length === 0) {
                        try {
                            localStorage.removeItem('crm_data_cache');
                            localStorage.removeItem('crm_customers_cache');
                            localStorage.removeItem('crm_data_cache_time');
                            localStorage.removeItem('crm_last_sync');
                        } catch (e) { }
                    } else {
                        // In LocalStorage cachen (nur wenn Daten vorhanden)
                        try {
                            localStorage.setItem('crm_data_cache', JSON.stringify(normalized));
                            localStorage.setItem('crm_data_cache_time', Date.now().toString());
                            localStorage.setItem('crm_last_sync', String(Date.now()));
                        } catch (e) { }
                    }

                    clearTimeout(syncTimeout);
                    syncInProgressRef.current = false;
                    setIsImporting(false);
                    setSyncStatus('success');
                    setLastSynced(Date.now());

                    if (!isAuto) {
                        showToast(`${normalized.length} Kunden aus Backend geladen.`);
                    }

                    resolve(normalized.length);
                })
                .catch((e) => {
                    clearTimeout(syncTimeout);
                    syncInProgressRef.current = false;
                    setIsImporting(false);
                    setSyncStatus('error');
                    setImportError(isAuto ? null : (e?.message || 'Backend nicht erreichbar.'));
                    if (!isAuto) {
                        showToast('Kundenliste nicht geladen. Backend nicht erreichbar.', 'error');
                    }
                    reject(e);
                });
        });
    };

    // Reset hasAutoImported when authentication changes
    useEffect(() => {
        if (!isAuthenticated) {
            hasAutoImported.current = false;
        }
    }, [isAuthenticated]);

    // Sobald jemand eingeloggt ist: immer zuerst Cache anzeigen, dann im Hintergrund aktualisieren
    useEffect(() => {
        if (!isAuthenticated) return;
        try {
            const cached = localStorage.getItem('crm_data_cache') || localStorage.getItem('crm_customers_cache');
            if (cached) {
                const arr = JSON.parse(cached);
                // Cache nur verwenden, wenn Daten vorhanden sind
                if (Array.isArray(arr) && arr.length > 0) {
                    setRawCustomers(arr);
                } else {
                    // Cache löschen, wenn leer
                    localStorage.removeItem('crm_data_cache');
                    localStorage.removeItem('crm_customers_cache');
                    localStorage.removeItem('crm_data_cache_time');
                }
            }
        } catch (e) { }
        if (!hasAutoImported.current) {
            hasAutoImported.current = true;
            fetchKundenlisteData(true).catch(() => 0);
        }
    }, [isAuthenticated]);

    // Update currentUser wenn sich users ändern (z.B. nach User-Edit)
    useEffect(() => {
        if (currentUser && users.length > 0) {
            const updatedUser = users.find(u => u.id === currentUser.id);
            if (updatedUser) {
                // Prüfe mehrere Felder, nicht nur level
                const hasChanges = updatedUser.level !== currentUser.level ||
                    updatedUser.role !== currentUser.role ||
                    updatedUser.canEditRoster !== currentUser.canEditRoster ||
                    updatedUser.canManageGoals !== currentUser.canManageGoals;
                if (hasChanges) {
                    console.log('Updating currentUser from users array:', {
                        oldLevel: currentUser.level,
                        newLevel: updatedUser.level
                    });
                    setCurrentUser({ ...currentUser, ...updatedUser });
                }
            }
        }
    }, [users]);


    // Excel-Import über Backend-API
    const onImportExcel = async (e: any) => {
        const file = e.target.files?.[0];
        if (!file) return;

        // Prüfe Dateityp
        const validTypes = [
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
            'application/vnd.ms-excel', // .xls
            'application/vnd.ms-excel.sheet.macroEnabled.12', // .xlsm
        ];
        const validExtensions = ['.xlsx', '.xls', '.xlsm'];
        const fileName = file.name.toLowerCase();
        const hasValidExtension = validExtensions.some(ext => fileName.endsWith(ext));

        if (!validTypes.includes(file.type) && !hasValidExtension) {
            showToast('Bitte wählen Sie eine Excel-Datei (.xlsx, .xls)', 'error');
            return;
        }

        // Prüfe Backend-Verfügbarkeit
        // Im Development-Modus automatisch localhost verwenden
        let CUSTOMERS_API_URL = 'http://localhost:3000'; // Default für Development
        let CUSTOMERS_API_SECRET = '';

        // Sicherer Zugriff auf import.meta.env
        if (typeof import.meta !== 'undefined' && import.meta.env) {
            const env = import.meta.env;
            const isDev = env.DEV === true || env.MODE === 'development';

            if (!isDev && env.VITE_VERLAENGERUNG_BACKEND_URL) {
                CUSTOMERS_API_URL = env.VITE_VERLAENGERUNG_BACKEND_URL;
            } else if (isDev) {
                CUSTOMERS_API_URL = '';
            }

            if (env.VITE_VERLAENGERUNG_API_SECRET) {
                CUSTOMERS_API_SECRET = env.VITE_VERLAENGERUNG_API_SECRET;
            }
        }

        if (!CUSTOMERS_API_SECRET) {
            showToast('Backend nicht konfiguriert. Bitte .env prüfen.', 'error');
            return;
        }

        setIsUploadingExcel(true);
        setExcelImportResult(null);
        setImportError(null);

        try {
            const formData = new FormData();
            formData.append('file', file);

            // API-Secret als Query-Parameter senden (einfacher bei FormData)
            let importUrl = `${CUSTOMERS_API_URL}/api/customers/import-excel`;
            if (CUSTOMERS_API_SECRET) {
                const separator = importUrl.includes('?') ? '&' : '?';
                importUrl = `${importUrl}${separator}apiSecret=${encodeURIComponent(CUSTOMERS_API_SECRET)}`;
            }

            const response = await fetch(importUrl, {
                method: 'POST',
                body: formData
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({ error: 'Unbekannter Fehler' }));
                throw new Error(errorData.error || `HTTP ${response.status}`);
            }

            const result = await response.json();
            setExcelImportResult(result);
            setIsUploadingExcel(false);
            setSyncStatus('success');
            setLastSynced(Date.now());

            // Erfolgsmeldung
            const totalDuplicates = (result.customerDuplicates || 0) + (result.contractDuplicates || 0);
            const message = `Import abgeschlossen: ${result.imported} neue Kunden, ${result.updated} aktualisiert, ${result.contractsCount} Verträge hinzugefügt${result.contractsSkipped > 0 ? `, ${result.contractsSkipped} Vertrags-Duplikate übersprungen` : ''}.${totalDuplicates > 0 ? ` ${totalDuplicates} Duplikate gefunden.` : ''}`;
            showToast(message, totalDuplicates > 0 ? 'warning' : 'success');

            addLog('Excel-Import', `${result.imported} neue, ${result.updated} aktualisiert, ${result.contractsCount} Verträge, ${totalDuplicates} Duplikate`, 'import');

            // Kundenliste neu laden
            if (CUSTOMERS_API_URL && CUSTOMERS_API_SECRET) {
                try {
                    let customersUrl = `${CUSTOMERS_API_URL}/api/customers`;
                    const separator = customersUrl.includes('?') ? '&' : '?';
                    customersUrl = `${customersUrl}${separator}apiSecret=${encodeURIComponent(CUSTOMERS_API_SECRET)}`;
                    const res = await fetch(customersUrl);
                    if (res.ok) {
                        const data = await res.json();
                        const customers = data.customers || [];
                        const normalized = customers.map((c: any) => ({
                            ...c,
                            verträge: c.verträge || c.vertraege || [],
                            vertraege: c.vertraege || c.verträge || []
                        }));
                        setRawCustomers(normalized);
                        try {
                            localStorage.setItem('crm_data_cache', JSON.stringify(normalized));
                            localStorage.setItem('crm_data_cache_time', Date.now().toString());
                        } catch (e) { }
                    }
                } catch (e) {
                    console.error('[Excel-Import] Fehler beim Neuladen der Kundenliste:', e);
                }
            }
        } catch (error: any) {
            setIsUploadingExcel(false);
            setSyncStatus('error');
            setImportError(error.message || 'Fehler beim Excel-Import');
            showToast('Fehler beim Excel-Import: ' + (error.message || 'Unbekannter Fehler'), 'error');
            console.error('[Excel-Import]', error);
        }
    };

    const deleteCustomer = useCallback(async (id: any) => {
        try {
            setRawCustomers((prev: any) => {
                const updated = prev.filter((c: any) => c.id !== id);
                try {
                    localStorage.setItem('crm_data_cache', JSON.stringify(updated));
                    localStorage.setItem('crm_data_cache_time', Date.now().toString());
                    localStorage.setItem('crm_customers_cache', JSON.stringify(updated));
                    localStorage.setItem('crm_customers_cache_time', Date.now().toString());
                } catch (e) { }
                return updated;
            });
            showToast("Kunde gelöscht.");
            addLog('Kunde gelöscht', `Kunde-ID: ${id}`, 'customer', id);
        } catch (err) {
            showToast("Fehler beim Löschen", "error");
        }
    }, []);

    const saveCustomer = useCallback(async (_customer: any) => {
        // Kundenbearbeitung/-anlage ist deaktiviert (Read-only CRM-Ansicht)
        showToast("Kunden anlegen und bearbeiten ist deaktiviert.", "error");
        return false;
    }, []);

    const addNote = async (cid: any, txt: any) => {
        if (!firebaseUser || !db || !cid) return;
        const trimmed = String(txt ?? '').trim();
        if (!trimmed) return;
        const newNotes = [...(notesMap[cid] || []), { text: trimmed, author: currentUser?.name ?? 'Unbekannt', date: new Date().toLocaleDateString('de-DE') }];
        setNotesMap((prev: any) => ({ ...prev, [cid]: newNotes }));
        try {
            await setDoc(getFirestoreDoc('customer_notes', cid), { notes: newNotes });
            addLog('Notiz hinzugefügt', `Kunde-ID: ${cid}`, 'customer', cid);
        } catch (e: any) {
            setNotesMap((prev: any) => ({ ...prev, [cid]: (prev[cid] || []).slice(0, -1) }));
            showToast('Notiz konnte nicht gespeichert werden.', 'error');
        }
    };

    const addTask = async (data: any) => {
        if (firebaseUser) {
            await addDoc(getFirestoreColl('tasks'), {
                ...data,
                status: 'pending',
                assignedTo: data.assignedTo || currentUser?.name || 'Alle',
                createdAt: new Date().toISOString()
            });
            setShowTaskModal(false);
            setNewTaskDefaults({});
            addLog('Aufgabe erstellt', `${data.title}`, 'task');
        }
    };
    const updateTask = async (id: any, st: any) => {
        if (!firebaseUser) {
            showToast('Bitte anmelden, um Aufgaben zu aktualisieren.', 'error');
            return;
        }
        setTasks((prev) => prev.map((t: any) => (t.id === id ? { ...t, status: st } : t)));
        try {
            await updateDoc(getFirestoreDoc('tasks', id), { status: st });
            addLog('Aufgabe aktualisiert', `Status: ${st}`, 'task', id);
        } catch (e: any) {
            const previousStatus = st === 'done' ? 'pending' : 'done';
            setTasks((prev) => prev.map((t: any) => (t.id === id ? { ...t, status: previousStatus } : t)));
            showToast('Aufgabe konnte nicht aktualisiert werden.', 'error');
        }
    };
    const deleteTask = async (id: any) => {
        if (!firebaseUser) {
            showToast('Bitte anmelden, um Aufgaben zu löschen.', 'error');
            return;
        }
        try {
            await deleteDoc(getFirestoreDoc('tasks', id));
            setTasks((prev) => prev.filter((t: any) => t.id !== id));
            addLog('Aufgabe gelöscht', `Task-ID: ${id}`, 'task', id);
        } catch (e: any) {
            showToast('Aufgabe konnte nicht gelöscht werden.', 'error');
        }
    };

    const canManageRoster = currentUser?.role === 'admin' || (currentUser as any)?.canEditRoster;

    const saveShift = async (d: any) => {
        if (!firebaseUser || !canManageRoster) {
            showToast('Keine Berechtigung für Dienstplan-Aktionen', 'error');
            return;
        }
        const { id, ...rest } = d;
        if (id) {
            // Bestehende Schicht aktualisieren (Firestore + lokaler State)
            await updateDoc(getFirestoreDoc('roster_shifts', id), rest);
            setShifts((prev: any[]) =>
                prev.map((s: any) => (s.id === id ? { ...s, ...rest } : s))
            );
            addLog('Schicht bearbeitet', `${rest.date}`, 'shift', id);
        } else {
            // Neue Schicht anlegen (Firestore + lokaler State)
            const ref = await addDoc(getFirestoreColl('roster_shifts'), rest);
            setShifts((prev: any[]) => [...prev, { ...rest, id: ref.id }]);
            addLog('Schicht erstellt', `${rest.date}`, 'shift');
        }
    };

    const deleteShift = async (id: any) => {
        if (!firebaseUser || !canManageRoster) {
            showToast('Keine Berechtigung für Dienstplan-Aktionen', 'error');
            return;
        }
        await deleteDoc(getFirestoreDoc('roster_shifts', id));
        // Lokalen Dienstplan-State sofort aktualisieren
        setShifts((prev: any[]) => prev.filter((s: any) => s.id !== id));
        addLog('Schicht gelöscht', `Shift-ID: ${id}`, 'shift', id);
    };
    const requestSwapShift = async (shiftId: any, toUserId: any) => {
        if (!firebaseUser || !db || !currentUser?.id || !canManageRoster) {
            showToast('Keine Berechtigung für Dienstplan-Aktionen', 'error');
            return;
        }
        const targetUser = users.find((u: any) => u.id === toUserId);
        await updateDoc(getFirestoreDoc('roster_shifts', shiftId), {
            swapRequest: {
                toUserId,
                requestedBy: currentUser.id,
                status: 'pending',
                requestedAt: new Date().toISOString()
            }
        });
        addLog('Schicht-Tausch angefragt', `Anfrage an ${targetUser?.name || 'Mitarbeiter'}`, 'shift', shiftId);
    };
    const respondSwapShift = async (shift: any, action: 'accept' | 'decline' | 'cancel') => {
        if (!firebaseUser || !db || !canManageRoster) {
            showToast('Keine Berechtigung für Dienstplan-Aktionen', 'error');
            return;
        }
        const ref = getFirestoreDoc('roster_shifts', shift.id);
        if (action === 'accept' && shift.swapRequest?.toUserId) {
            await updateDoc(ref, { userId: shift.swapRequest.toUserId, swapRequest: deleteField() });
            addLog('Schicht-Tausch akzeptiert', `Schicht vom ${shift.date}`, 'shift', shift.id);
        } else if (action === 'decline') {
            await updateDoc(ref, { swapRequest: deleteField() });
            addLog('Schicht-Tausch abgelehnt', `Schicht vom ${shift.date}`, 'shift', shift.id);
        } else if (action === 'cancel') {
            await updateDoc(ref, { swapRequest: deleteField() });
            addLog('Schicht-Tausch abgebrochen', `Schicht vom ${shift.date}`, 'shift', shift.id);
        }
    };
    const addVacation = async (data: any) => {
        if (!firebaseUser || !db || !currentUser?.id || !canManageRoster) {
            showToast('Keine Berechtigung für Dienstplan-Aktionen', 'error');
            return false;
        }
        const { startDate, endDate } = data;
        if (!startDate || !endDate) { showToast('Bitte Start- und Enddatum wählen', 'error'); return false; }
        if (new Date(startDate) > new Date(endDate)) { showToast('Startdatum darf nicht nach dem Enddatum liegen', 'error'); return false; }
        const docData = {
            userId: currentUser.id,
            userName: currentUser.name,
            startDate,
            endDate,
            createdAt: new Date().toISOString()
        };
        const ref = await addDoc(getFirestoreColl('vacations'), docData);
        setVacations(prev => [...prev, { id: ref.id, ...docData }]);
        showToast('Urlaub eingetragen.');
        addLog('Urlaub eingetragen', `${startDate} bis ${endDate}`, 'vacation');
        return true;
    };
    const deleteVacation = async (vacationId: any, ownerId?: any) => {
        if (!firebaseUser || !db || !currentUser?.id || !canManageRoster) {
            showToast('Keine Berechtigung für Dienstplan-Aktionen', 'error');
            return false;
        }
        await deleteDoc(getFirestoreDoc('vacations', vacationId));
        setVacations(prev => prev.filter(v => v.id !== vacationId));
        showToast('Urlaub gelöscht.');
        addLog('Urlaub gelöscht', `Vacation-ID: ${vacationId}`, 'vacation', vacationId);
        return true;
    };
    const toggleClosing = async (d: any) => {
        const ref = getFirestoreDoc('roster_closings', 'list');
        const snap = await getDoc(ref);
        let data = snap.exists() ? snap.data() : {};
        const dataAny = data as any;
        let dates = Array.isArray(dataAny?.dates) ? dataAny.dates : [];
        await setDoc(ref, { dates: dates.includes(d) ? dates.filter((x: any) => x !== d) : [...dates, d] });
    };
    const toggleContractStatus = async (id: any, st: any) => {
        if (!firebaseUser || !db || id == null) return;
        try {
            await setDoc(getFirestoreDoc('contract_states', String(id)), { status: st });
        setContractStates((prev: Record<string, any>) => ({ ...prev, [String(id)]: st }));
            addLog('Ablauf aktualisiert', `Status: ${st}`, 'contract', id);
        } catch (e: any) {
            console.warn('[toggleContractStatus] Firestore write failed:', e?.message);
        }
    };
    const cleanupContractStates = async () => {
        if (!firebaseUser || !db) return;
        try {
            const allContractIds = new Set(rawCustomers.flatMap((c: any) => (c.verträge || []).map((v: any) => v.id).filter(Boolean)));
            const snap = await getDocs(getFirestoreColl('contract_states'));
            const toDelete = snap.docs.filter((d: any) => !allContractIds.has(d.id));
            for (const d of toDelete) await deleteDoc(d.ref);
            setContractStates((prev: Record<string, any>) => {
                const next = { ...prev };
                toDelete.forEach((d: any) => { delete next[d.id]; });
                return next;
            });
            addLog('Ablauf-Cleanup', `${toDelete.length} verwaiste Einträge gelöscht`, 'settings');
            showToast(toDelete.length > 0 ? `${toDelete.length} verwaiste Ablauf-Einträge gelöscht.` : 'Keine verwaisten Einträge.', 'success');
        } catch (e: any) {
            showToast('Cleanup fehlgeschlagen: ' + (e?.message || e), 'error');
        }
    };

    const savePassword = async (data: any) => {
        if (!firebaseUser || !db) return;
        try {
            if (data.id) {
                const { id, ...rest } = data;
                await updateDoc(getFirestoreDoc('passwords', id), cleanUndefined(rest));
                addLog('Passwort bearbeitet', `${data.name}`, 'password', id);
            } else {
                await addDoc(getFirestoreColl('passwords'), cleanUndefined(data));
                addLog('Passwort hinzugefügt', `${data.name}`, 'password');
            }
            setShowPwdModal(false);
            const snap = await getDocs(getFirestoreColl('passwords'));
            setPasswords(snap.docs.map((d: any) => ({ id: d.id, ...d.data() })));
            showToast('Passwort gespeichert.', 'success');
        } catch (e: any) {
            showToast('Speichern fehlgeschlagen: ' + (e?.message || e), 'error');
        }
    };
    const delPwd = async (id: any) => { if (firebaseUser && db) { await deleteDoc(getFirestoreDoc('passwords', id)); addLog('Passwort gelöscht', `Password-ID: ${id}`, 'password', id); setPasswords(prev => prev.filter((p: any) => p.id !== id)); } };
    const processPwdCSV = (file: any) => { if (!file) return; const reader = new FileReader(); reader.onload = (evt: any) => { Papa.parse(evt.target.result, { header: true, skipEmptyLines: 'greedy', complete: async (results) => { if (!firebaseUser || !db) return; const batch = writeBatch(db); results.data.forEach((row: any) => { const name = row['Name'] || row['Portal'] || row['Titel']; if (name) { const docRef = doc(getFirestoreColl('passwords')); batch.set(docRef, { name: name, uNummer: row['Benutzer'] || '', passwort: row['Passwort'] || '', link: row['Link'] || '', info: row['Info'] || '', createdAt: new Date().toISOString() }); } }); await batch.commit(); showToast('Passwörter importiert.'); } }); }; reader.readAsText(file); };

    const cleanupDuplicates = async () => showToast("Bereinigung gestartet...", "info");

    const activeUsers = useMemo(() => users.filter((u: any) => !u.hidden), [users]);
    const navItems = useMemo(() => {
        // Die aktuelle Ansicht bestimmen (adminView: 'admin' | 'employee')
        // und viewUser analog zu DashboardViewBase
        const isAdminView = adminView === 'admin';
        const effectiveRole = (currentUser?.role === 'admin' && !isAdminView) ? 'employee' : currentUser?.role;
        const isInnendienst = (currentUser?.level || '').toString().toLowerCase() === 'innendienst';
        const items = [
            // Kernbereich (Übersicht & CRM)
            { id: 'dashboard', icon: Shield, label: 'Übersicht', category: 'Übersicht & CRM' },
            { id: 'customers', icon: Users, label: 'Kunden', category: 'Übersicht & CRM' },
            ...(isInnendienst ? [] : [{ id: 'bestand' as const, icon: TrendingUp, label: 'Bestand', category: 'Übersicht & CRM' }]),

            // Dienstplan zu Workflows & Team
            { id: 'roster', icon: Calendar, label: 'Dienstplan', category: 'Workflows & Team' },
        ];

        // Ziele (optional)
        if (currentUser) {
            items.push({ id: 'goals', icon: Target, label: 'Ziele', category: 'Finanzen & Planung' });
        }

        // Finanzbereich: Provisionen + Boni – bei Innendienst ausblenden
        if (!isInnendienst) {
            items.push({ id: 'provisionen', icon: Calculator, label: 'Provisionen', category: 'Finanzen & Planung' });
            items.push({ id: 'boni', icon: Gift, label: 'Boni-Rechner', category: 'Finanzen & Planung' });
        }

        // Workflows
        items.push(
            { id: 'tasks', icon: CheckCircle, label: 'Aufgaben', category: 'Workflows & Team' },
            { id: 'expirations', icon: AlertCircle, label: 'Abläufe', category: 'Workflows & Team' }
        );
        // Admin sieht Verlängerung immer, sonst nur mit Berechtigung
        if (currentUser?.role === 'admin' || currentUser?.canSendVerlaengerungEmail) {
            items.push({ id: 'verlaengerung', icon: MailIcon, label: 'Verlängerung', category: 'Workflows & Team' });
        }

        // Persönliche Info
        items.push(
            { id: 'birthdays', icon: Cake, label: 'Geburtstage', category: 'Tools & Verwaltung' },
            { id: 'phonebook', icon: Phone, label: 'Telefonbuch', category: 'Tools & Verwaltung' }
        );

        // Verwaltung
        items.push({ id: 'passwords', icon: Lock, label: 'Passwörter', category: 'Tools & Verwaltung' });

        if (currentUser) {
            items.push({ id: 'users', icon: User, label: 'Team', category: 'Workflows & Team' });
        }

        items.push({ id: 'settings', icon: Settings, label: 'Einstellungen', category: 'Tools & Verwaltung' });

        // Admin-Bereich (separiert am Ende) – nur im Admin-Modus anzeigen!
        if (effectiveRole === 'admin' || currentUser?.canViewStats) {
            if (effectiveRole === 'admin') {
                items.push({ id: 'activity', icon: Activity, label: 'Protokoll', category: 'Tools & Verwaltung' });
            }
            items.push({ id: 'stats', icon: BarChart3, label: 'Statistik', category: 'Tools & Verwaltung' });
            if (effectiveRole === 'admin') {
                items.push({ id: 'templates', icon: FileText, label: 'Briefvorlagen', category: 'Tools & Verwaltung' });
                items.push({ id: 'boni_tables', icon: Table2, label: 'Boni-Tabellen', category: 'Tools & Verwaltung' });
                items.push({ id: 'admin', icon: Upload, label: 'Import', category: 'Tools & Verwaltung' });
            }
        }

        return items;
    }, [currentUser, adminView]);

    const saveUser = async (userData: any) => {
        if (!firebaseUser || !db || !currentUser) return;
        const isAdmin = currentUser.role === 'admin';
        if (!isAdmin) {
            if (!userData.id || userData.id !== currentUser.id) {
                showToast('Du kannst nur dein eigenes Profil bearbeiten.', 'error');
                setShowUserModal(false);
                return;
            }
        }
        try {
            // Rolle immer aus userData übernehmen, wenn Admin
            let newRole = isAdmin ? (userData.role || 'employee') : currentUser.role;
            // Fallback: falls userData.role fehlt, alte Rolle übernehmen
            if (!userData.role && userData.id) {
                const oldUser = users.find((u: any) => u.id === userData.id);
                if (oldUser && oldUser.role) newRole = oldUser.role;
            }
            const payload = {
                ...userData,
                level: userData.level || '',
                role: newRole,
                canEditRoster: isAdmin ? !!userData.canEditRoster : currentUser.canEditRoster,
                canManageGoals: isAdmin ? !!userData.canManageGoals : currentUser.canManageGoals,
                canViewStats: isAdmin ? !!(userData as any).canViewStats : (currentUser as any).canViewStats,
                allowedToView: isAdmin ? editingUserAllowedToView : currentUser.allowedToView
            };
            const uid = userData.id || 'user-' + Math.random().toString(36).substr(2, 9);
            payload.id = uid;

            // In Firestore speichern (gleicher Pfad wie beim Laden)
            const userRef = getFirestoreDoc('users', uid);
            const { id: _id, ...dataForFirestore } = payload;
            const clean = cleanUndefined(dataForFirestore);
            if (users.some((u: any) => u.id === uid)) {
                await updateDoc(userRef, clean);
            } else {
                await setDoc(userRef, clean);
            }

            // Lokale Nutzerliste aktualisieren
            setUsers((prev: any[]) => {
                const idx = prev.findIndex((u: any) => u.id === uid);
                const next = { ...payload };
                if (idx >= 0) {
                    const nextArr = [...prev];
                    nextArr[idx] = next;
                    return nextArr;
                }
                return [...prev, next];
            });

            // Falls der aktuelle User bearbeitet wurde, auch currentUser sofort aktualisieren
            if (uid === currentUser.id) {
                setCurrentUser({ ...currentUser, ...payload });
            }
            showToast("Mitarbeiter gespeichert.");
            addLog('Mitarbeiter gespeichert', `${payload.name}`, 'user', uid);
            setShowUserModal(false);
        } catch (err) {
            console.error('[saveUser]', err);
            showToast("Fehler beim Speichern", 'error');
        }
    };

    const deleteUser = async (uid: any) => { if (firebaseUser && db) { await deleteDoc(getFirestoreDoc('users', uid)); addLog('Mitarbeiter gelöscht', `User-ID: ${uid}`, 'user', uid); } };
    const toggleUserVisibility = async (u: any) => { if (firebaseUser && db) await updateDoc(getFirestoreDoc('users', u.id), { hidden: !u.hidden }); };
    const openPermissionsModal = (user: any) => { setPermissionsTargetUser(user); setShowPermissionsModal(true); };
    const togglePermission = async (targetId: any) => { if (!permissionsTargetUser || !firebaseUser || !db) return; const currentAllowed = permissionsTargetUser.allowedToView || []; let newAllowed; if (currentAllowed.includes(targetId)) newAllowed = currentAllowed.filter((id: any) => id !== targetId); else newAllowed = [...currentAllowed, targetId]; await updateDoc(getFirestoreDoc('users', permissionsTargetUser.id), { allowedToView: newAllowed }); setPermissionsTargetUser({ ...permissionsTargetUser, allowedToView: newAllowed }); };

    // Goal Management
    const saveGoal = async () => {
        if (!firebaseUser || !db || !goalFormData.userId || !currentUser) return;
        try {
            const currentYear = new Date().getFullYear();
            const payload = {
                userId: goalFormData.userId,
                userName: goalFormData.userName,
                year: currentYear,
                target: parseFloat(goalFormData.target) || 0,
                current: parseFloat(goalFormData.current) || 0,
                description: goalFormData.description || '',
                lastUpdated: new Date().toISOString(),
                updatedBy: currentUser.name
            };

            if (goalFormData.id) {
                await updateDoc(getFirestoreDoc('goals', goalFormData.id), payload);
            } else {
                await addDoc(getFirestoreColl('goals'), {
                    ...payload,
                    createdAt: new Date().toISOString()
                });
            }

            showToast("Ziel gespeichert.");
            addLog('Ziel gespeichert', `${goalFormData.userName}: ${goalFormData.target} WS`, 'goal', goalFormData.id || 'new');
            setShowGoalModal(false);
            setGoalFormData({});
        } catch (err) {
            console.error(err);
            showToast("Fehler beim Speichern", 'error');
        }
    };

    const openGoalModal = (user: any, existingGoal: any) => {
        setGoalFormData({
            id: existingGoal?.id || '',
            userId: user.id,
            userName: user.name,
            target: existingGoal?.target || '',
            current: existingGoal?.current || '',
            description: existingGoal?.description || ''
        });
        setShowGoalModal(true);
    };

    const saveGoalDirectly = async (user: any, existingGoal: any, values: any) => {
        if (!firebaseUser || !db || !currentUser) return;
        try {
            const currentYear = new Date().getFullYear();
            const payload = {
                userId: user.id,
                userName: user.name,
                year: currentYear,
                target: values.target || 0,
                current: values.current || 0,
                description: values.description || '',
                lastUpdated: new Date().toISOString(),
                updatedBy: currentUser.name
            };

            if (existingGoal?.id) {
                await updateDoc(getFirestoreDoc('goals', existingGoal.id), payload);
                setGoals(prev => prev.map(g => g.id === existingGoal.id ? { ...g, ...payload } : g));
            } else {
                const ref = await addDoc(getFirestoreColl('goals'), {
                    ...payload,
                    createdAt: new Date().toISOString()
                });
                setGoals(prev => [...prev, { id: ref.id, ...payload }]);
            }

            showToast("Ziel gespeichert.");
            addLog('Ziel direkt gespeichert', `${user.name}: ${values.target} WS`, 'goal', existingGoal?.id || 'new');
        } catch (err) {
            console.error(err);
            showToast("Fehler beim Speichern", 'error');
        }
    };

    // Template Management
    const saveTemplate = async (templateData: any) => {
        if (!firebaseUser || !db) return;
        try {
            const { id, ...dataWithoutId } = templateData;
            if (id) {
                await updateDoc(getFirestoreDoc('letter_templates', id), cleanUndefined(dataWithoutId));
            } else {
                await addDoc(getFirestoreColl('letter_templates'), {
                    ...dataWithoutId,
                    createdAt: new Date().toISOString(),
                    createdBy: currentUser?.name || 'System'
                });
            }
            showToast("Vorlage gespeichert.");
        } catch (err) {
            console.error(err);
            showToast("Fehler beim Speichern", 'error');
        }
    };

    const deleteTemplate = async (templateId: string) => {
        if (!firebaseUser || !db) return;
        try {
            await deleteDoc(getFirestoreDoc('letter_templates', templateId));
            showToast("Vorlage gelöscht.");
        } catch (err) {
            console.error(err);
            showToast("Fehler beim Löschen", 'error');
        }
    };

    const fillTemplateContent = useCallback((content: string, customer: any) => {
        const anrede = customer.anrede === 'm' ? 'Herr' : customer.anrede === 'w' ? 'Frau' : (customer.anrede || '');
        const today = new Date().toLocaleDateString('de-DE');
        return (content || '')
            .replace(/{VORNAME}/gi, customer.vorname || '')
            .replace(/{NAME}/gi, customer.name || '')
            .replace(/{ANREDE}/gi, anrede)
            .replace(/{STRASSE}/gi, customer.strasse || '')
            .replace(/{PLZ}/gi, customer.plz || '')
            .replace(/{ORT}/gi, customer.ort || '')
            .replace(/{GEBURTSDATUM}/gi, customer.gebDatum || '')
            .replace(/{MITARBEITER}/gi, currentUser?.name || '')
            .replace(/{DATUM}/gi, today)
            .replace(/{HEUTE}/gi, today)
            .replace(/{KUNDENNUMMER}/gi, customer.id || '');
    }, [currentUser]);

    const generateLettersPdf = useCallback(async () => {
        if (isGeneratingLetters) return;
        if (!selectedTemplateId) {
            showToast('Bitte Vorlage auswählen.', 'error');
            return;
        }
        if (selectedCustomersForLetters.length === 0) {
            showToast('Bitte mindestens einen Kunden auswählen.', 'error');
            return;
        }
        if (selectedCustomersForLetters.length > 100) {
            showToast('Maximal 100 Kunden pro Batch', 'error');
            return;
        }

        const template = letterTemplates.find((t: any) => t.id === selectedTemplateId);
        if (!template) {
            showToast('Vorlage nicht gefunden', 'error');
            return;
        }

        setIsGeneratingLetters(true);
        setPdfGenerationProgress(0);

        try {
            // Dummy: kein echtes PDF
            const doc = { save: () => { } };
            const letters = selectedCustomersForLetters.map((c: any) => ({
                customer: c,
                html: fillTemplateContent(template.content || '', c)
            }));

            for (let i = 0; i < letters.length; i++) {
                const wrapper = document.createElement('div');
                wrapper.style.width = '210mm';
                wrapper.style.padding = '15mm';
                wrapper.style.boxSizing = 'border-box';
                wrapper.style.fontFamily = 'Arial, sans-serif';
                wrapper.style.fontSize = '12pt';
                wrapper.innerHTML = letters[i].html;
                document.body.appendChild(wrapper);


                // Dummy: PDF-Seite hinzufügen und rendern simulieren
                // (In echter Umgebung: doc.addPage(), doc.html(...))

                document.body.removeChild(wrapper);
                setPdfGenerationProgress(Math.round(((i + 1) / letters.length) * 100));
            }

            const fileName = `briefe-${template.type || 'letter'}-${selectedCustomersForLetters.length}.pdf`;
            // Dummy: PDF speichern simulieren
            // (In echter Umgebung: doc.save(fileName))
            showToast(`PDF mit ${selectedCustomersForLetters.length} Briefen erstellt.`, 'success');
        } catch (err) {
            console.error('PDF Generation Error', err);
            showToast('Fehler beim PDF-Erstellen', 'error');
        } finally {
            setIsGeneratingLetters(false);
            setPdfGenerationProgress(0);
        }
    }, [fillTemplateContent, isGeneratingLetters, letterTemplates, selectedCustomersForLetters, selectedTemplateId, showToast]);

    const openPreviewModal = useCallback(() => {
        if (!selectedTemplateId || selectedCustomersForLetters.length === 0) {
            showToast('Bitte Vorlage und Kunden auswählen.', 'error');
            return;
        }
        setPreviewCustomer(selectedCustomersForLetters[0]);
        setShowPdfPreview(true);
    }, [selectedTemplateId, selectedCustomersForLetters, showToast]);

    const trackRecentCustomer = useCallback((customer: any) => {
        if (!customer) return;
        setRecentCustomers((prev: any[] = []) => {
            const filtered = prev.filter((c: any) => c.id !== customer.id);
            return [customer, ...filtered].slice(0, 5);
        });
    }, []);

    useEffect(() => {
        if (selectedCustomer) {
            trackRecentCustomer(selectedCustomer);
        }
    }, [selectedCustomer, trackRecentCustomer]);

    useEffect(() => {
        const media = window.matchMedia('(max-width: 1023px)');
        const apply = () => setIsMobile(media.matches);
        apply();
        media.addEventListener('change', apply);
        return () => media.removeEventListener('change', apply);
    }, []);

    useEffect(() => {
        if (selectedCustomer && isMobile) {
            setShowCustomerDetailModal(true);
        }
    }, [selectedCustomer, isMobile]);

    const displayedExpirations = useMemo(() => {
        return [];
    }, [currentUser, rawCustomers, users, contractStates, expTab, expDateFrom, expDateTo, expSparteFilter]);

    // Login-Guard: Wenn nicht eingeloggt, LoginView anzeigen (nach allen Hooks!)
    if (!isAuthenticated) {
        const appVersion = typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : 'dev';
        return <LoginView onLogin={handleLogin} isLoading={isLoggingIn} error={loginError} loadingProgress={loadingProgress} appVersion={appVersion} debugStatus={undefined} />;
    }

    return (
        <>
            {confirmDialog && <ConfirmDialog open={!!confirmDialog} title={confirmDialog?.title} message={confirmDialog?.message ?? ''} confirmText={confirmDialog?.confirmText} variant={confirmDialog?.variant === 'danger' ? 'danger' : 'default'} onConfirm={() => { if (confirmDialog?.onConfirm) confirmDialog.onConfirm(); setConfirmDialog(null); }} onCancel={() => setConfirmDialog(null)} />}
            <GlobalSearch open={searchOpen} onClose={() => setSearchOpen(false)} query={searchQuery} onQueryChange={setSearchQuery} customers={rawCustomers} tasks={tasks} onSelectCustomer={setSelectedCustomer} onSelectTask={setShowTaskModal} onNavigate={setCurrentView} />
            {/* Tasks List Modal */}
            {/* {showTasksListModal && <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-gray-900/40 backdrop-blur-sm animate-fade-in"><div className="glass-panel rounded-[2rem] p-6 w-full max-w-4xl h-[85vh] shadow-2xl flex flex-col relative border border-white/10"><div className="flex justify-between items-center mb-6 pb-4 border-b border-white/10"><div><h3 className="text-2xl font-bold text-white drop-shadow-lg">Aufgabenverwaltung</h3><p className="text-sm text-white/70">Alle offenen und erledigten Aufgaben im Überblick</p></div><button onClick={() => setShowTasksListModal(false)} className="p-2 bg-white/10 hover:bg-white/20 rounded-full transition-colors"><X size={20} className="text-white"/></button></div><div className="flex-1 overflow-hidden"><TasksWidget tasks={tasks} currentUser={currentUser} onUpdateTask={updateTask} onDeleteTask={deleteTask} onShowTaskModal={setShowTaskModal} allUsers={users} isModal={true} /></div></div></div>} */}

            {/* Main Application Layout – ohne Sidebar */}
            <div className={`flex h-[100dvh] w-full overflow-hidden relative`}>
                {/* Mobile Menü Overlay */}
                {isMobileMenuOpen && (
                    <div className="md:hidden fixed inset-0 z-[100] flex animate-fade-in">
                        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-md" onClick={() => setIsMobileMenuOpen(false)} />
                        <div className="relative w-72 max-w-[85vw] h-full bg-white/90 dark:bg-slate-900/95 backdrop-blur-xl border-r border-white/20 shadow-2xl overflow-y-auto">
                            <div className="p-4 border-b border-slate-200/50 dark:border-white/10 flex justify-between items-center">
                                <span className="font-bold text-slate-900 dark:text-white">Menü</span>
                                <button onClick={() => setIsMobileMenuOpen(false)} className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-white/10">✕</button>
                            </div>
                            <nav className="p-4 space-y-1">
                                {navItems.map((item) => (
                                    <button
                                        key={item.id}
                                        onClick={() => { setCurrentView(item.id); setIsMobileMenuOpen(false); }}
                                        className={`w-full text-left px-4 py-3 rounded-xl text-sm font-semibold transition-all ${currentView === item.id ? 'bg-[#2F6BFF] text-white' : 'text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-white/10'}`}
                                    >
                                        {item.label}
                                    </button>
                                ))}
                            </nav>
                            <div className="p-4 border-t border-slate-200/50 dark:border-white/10">
                                <button onClick={() => { handleLogout(); setIsMobileMenuOpen(false); }} className="w-full px-4 py-3 rounded-xl text-sm font-semibold text-red-600 hover:bg-red-50 dark:hover:bg-red-500/10 flex items-center gap-2">
                                    <span>Abmelden</span>
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Main Content Area */}
                <div className="flex-1 flex flex-col min-w-0 h-[100dvh] overflow-hidden relative">
                    {/* CRM Header: Top-Nav + KPI-Karten (Salesforce-Style) */}
                    <CRMHeader
                        currentView={currentView}
                        setCurrentView={setCurrentView}
                        selectedCustomer={selectedCustomer}
                        currentUser={currentUser}
                        isDark={isDark}
                        onSearchOpen={() => setSearchOpen(true)}
                        customers={rawCustomers}
                        tasks={tasks}
                        navItems={navItems.map((n) => ({ id: n.id, label: n.label }))}
                        onMobileMenuOpen={() => setIsMobileMenuOpen(true)}
                        handleLogout={handleLogout}
                    />

                    {/* Content + Optional Right Detail Panel */}
                    <div className="flex-1 flex min-w-0 overflow-hidden">
                        <main className="flex-1 overflow-y-auto overflow-x-hidden relative bg-transparent scrollbar-premium">
                        <div className="flex-1 w-full max-w-[1240px] mx-auto p-4 md:p-6 lg:p-8 shrink-0">
                            <div className="w-full min-w-0 px-0 sm:px-0">
                                {/* Breadcrumbs (Punkt 5) */}
                                <div className="mb-4 sm:mb-6 flex flex-wrap items-center gap-2 sm:gap-3">
                                    <nav className={`flex flex-wrap items-center gap-1.5 sm:gap-2 text-xs sm:text-sm min-w-0 ${isDark ? 'text-white/60' : 'text-slate-600'}`} aria-label="Breadcrumb">
                                        {currentView !== 'dashboard' && (
                                            <button type="button" onClick={() => setSelectedCustomer(null)} className={`font-semibold capitalize hover:underline ${isDark ? 'text-white' : 'text-slate-800'}`}>{
                                                currentView === 'customers' ? 'Kunden' :
                                                    currentView === 'users' ? 'Team' :
                                                        currentView === 'roster' ? 'Dienstplan' :
                                                            currentView === 'goals' ? 'Ziele' :
                                                                currentView === 'phonebook' ? 'Telefonbuch' :
                                                                    currentView === 'activity' ? 'Protokoll' :
                                                                        currentView === 'stats' ? 'Statistik' :
                                                                            currentView === 'tasks' ? 'Aufgaben' :
                                                                                currentView === 'birthdays' ? 'Geburtstage' :
                                                                                    currentView === 'expirations' ? 'Abläufe' :
                                                                                        currentView === 'verlaengerung' ? 'Verlängerung' :
                                                                                            currentView === 'provisionen' ? 'Provisionen' :
                                                                                                currentView === 'templates' ? 'Vorlagen' :
                                                                                                    currentView === 'boni_tables' ? 'Boni-Tabellen' :
                                                                                                        currentView === 'admin' ? 'Import' :
                                                                                                            currentView === 'settings' ? 'Einstellungen' :
                                                                                                                currentView === 'passwords' ? 'Passwörter' :
                                                                                                                    currentView
                                            }</button>
                                        )}
                                        {selectedCustomer && (
                                            <>
                                                <ChevronRight size={16} className={isDark ? 'text-white/20' : 'text-slate-400'} />
                                                <span className={`font-semibold ${isDark ? 'text-white' : 'text-slate-800'}`}>{selectedCustomer.vorname} {selectedCustomer.name}</span>
                                            </>
                                        )}
                                    </nav>
                                </div>


                                {currentView === 'dashboard' && <DashboardView {...{ customers: rawCustomers, tasks, shifts, currentUser, goals, onSelectCustomer: setSelectedCustomer, onUpdateTask: updateTask, onDeleteTask: deleteTask, onShowTaskModal: setShowTaskModal, onOpenBirthdays: () => setShowBirthdayModal(true), onOpenExpirations: () => setShowExpirationModal(true), allUsers: users, contractStates, onToggleContractState: toggleContractStatus, onOpenTasks: () => setShowTasksListModal(true), onOpenCustomers: () => setCurrentView('customers'), onEditGoal: openGoalModal, Clock, onNavigateTo: setCurrentView, adminView, setAdminView, widgetOrder: dashboardWidgetOrder ?? ['customers', 'contracts', 'birthdays', 'expirations', 'verlaengerung', 'tasks', 'bestand', 'nextShift', 'reminders'], onSaveWidgetOrder: saveDashboardPreferences }} />}
                                {currentView === 'bestand-filter' && (
                                    <React.Suspense fallback={<div>Filter wird geladen...</div>}>
                                        <KundenKartei
                                            customers={rawCustomers}
                                            allSparten={Array.from(new Set(rawCustomers.flatMap((c: any) => c.verträge?.map((v: any) => v.sparte).filter(Boolean))))}
                                            currentUser={currentUser}
                                            allUsers={users}
                                            onSelectCustomer={setSelectedCustomer}
                                        />
                                    </React.Suspense>
                                )}
                                {currentView === 'roster' && <RosterView users={users} shifts={shifts} vacations={vacations} closings={closings} currentUser={currentUser} onSaveShift={saveShift} onDeleteShift={deleteShift} onToggleClosing={toggleClosing} onRequestSwap={requestSwapShift} onRespondSwap={respondSwapShift} onAddVacation={addVacation} onDeleteVacation={deleteVacation} />}
                                {currentView === 'bestand' && <BestandsView customers={rawCustomers} currentUser={currentUser} allUsers={users} onNavigateTo={setCurrentView} />}
                                {currentView === 'boni' && <BoniRechnerView />}
                                {currentView === 'customers' && <CustomersView customers={rawCustomers} notesMap={notesMap} onSelectCustomer={setSelectedCustomer} currentUser={currentUser} allUsers={users} onDeleteCustomer={deleteCustomer} requestConfirm={requestConfirm} syncStatus={syncStatus} lastSynced={lastSynced} onSync={() => { void fetchKundenlisteData(false).catch(() => 0); }} />}
                                {currentView === 'phonebook' && <PhonebookView currentUser={currentUser} />}
                                {/* Import (Admin): Excel-Upload */}
                                {currentView === 'admin' && currentUser?.role === 'admin' && (
                                    <PageLayout>
                                        <PageHeader title="Import" subtitle="Kundendaten aus Excel-Datei importieren" />
                                        <PageCard className="p-6 space-y-6">
                                            <div>
                                                <h3 className={`text-lg font-semibold mb-3 ${isDark ? 'text-white' : 'text-slate-800'}`}>Kundenliste aktualisieren</h3>
                                                <p className={`text-sm mb-3 ${isDark ? 'text-white/70' : 'text-slate-600'}`}>Lädt die Kundenliste aus der Backend-Datenbank.</p>
                                                <IOSButton onClick={() => { void fetchKundenlisteData(false).catch(() => 0); }} disabled={isImporting}>
                                                    {isImporting ? 'Lade…' : 'Jetzt synchronisieren'}
                                                </IOSButton>
                                            </div>
                                            <div>
                                                <h3 className={`text-lg font-semibold mb-3 ${isDark ? 'text-white' : 'text-slate-800'}`}>Excel-Datei (SQL-Datenbank)</h3>
                                                <p className={`text-sm mb-3 ${isDark ? 'text-white/70' : 'text-slate-600'}`}>Importiert Excel-Dateien direkt in die SQL-Datenbank. Duplikate werden automatisch erkannt und übersprungen.</p>
                                                <label className={`flex items-center justify-center gap-2 w-full max-w-xs px-4 py-4 rounded-xl border-2 border-dashed cursor-pointer transition-colors ${isUploadingExcel ? 'opacity-50 cursor-not-allowed' : ''} ${isDark ? 'border-blue-400/50 hover:bg-blue-500/10 text-blue-300' : 'border-blue-400 hover:bg-blue-50 text-blue-700'}`}>
                                                    {isUploadingExcel ? (
                                                        <>
                                                            <Loader2 size={20} className="animate-spin" />
                                                            <span className="font-medium">Import läuft…</span>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <FileSpreadsheet size={20} />
                                                            <span className="font-medium">Excel auswählen</span>
                                                        </>
                                                    )}
                                                    <input type="file" accept=".xlsx,.xls,.xlsm" className="hidden" onChange={onImportExcel} disabled={isUploadingExcel} />
                                                </label>
                                            </div>
                                            {(syncStatus !== 'idle' || importError || lastSynced || excelImportResult) && (
                                                <div className={`text-sm pt-2 border-t ${isDark ? 'border-white/20 text-white/70' : 'border-slate-200 text-slate-600'}`}>
                                                    {isImporting && <p>Import läuft…</p>}
                                                    {isUploadingExcel && <p>Excel-Import läuft…</p>}
                                                    {importError && <p className="text-red-500 font-medium">{importError}</p>}
                                                    {excelImportResult && (
                                                        <div className={`mt-3 p-3 rounded-lg ${isDark ? 'bg-blue-500/20 border border-blue-400/30' : 'bg-blue-50 border border-blue-200'}`}>
                                                            <p className={`font-semibold mb-2 ${isDark ? 'text-blue-300' : 'text-blue-900'}`}>Import abgeschlossen:</p>
                                                            <ul className={`space-y-1 text-xs ${isDark ? 'text-blue-200' : 'text-blue-800'}`}>
                                                                <li>✓ {excelImportResult.imported} neue Kunden importiert</li>
                                                                <li>✓ {excelImportResult.updated} bestehende Kunden aktualisiert</li>
                                                                <li>✓ {excelImportResult.contractsCount} neue Verträge hinzugefügt</li>
                                                                {excelImportResult.contractsSkipped > 0 && (
                                                                    <li className="text-amber-500">⚠ {excelImportResult.contractsSkipped} Vertrags-Duplikate übersprungen</li>
                                                                )}
                                                                {(excelImportResult.customerDuplicates > 0 || excelImportResult.contractDuplicates > 0) && (
                                                                    <li className="text-amber-500 font-semibold">⚠ {excelImportResult.customerDuplicates || 0} Kunden-Duplikate, {excelImportResult.contractDuplicates || 0} Vertrags-Duplikate gefunden</li>
                                                                )}
                                                            </ul>
                                                            {((excelImportResult.customerDuplicatesList && excelImportResult.customerDuplicatesList.length > 0) || (excelImportResult.contractDuplicatesList && excelImportResult.contractDuplicatesList.length > 0)) && (
                                                                <details className="mt-2">
                                                                    <summary className={`cursor-pointer text-xs font-semibold ${isDark ? 'text-blue-300' : 'text-blue-700'}`}>
                                                                        Duplikate anzeigen ({((excelImportResult.customerDuplicatesList?.length || 0) + (excelImportResult.contractDuplicatesList?.length || 0))})
                                                                    </summary>
                                                                    <div className="mt-2 space-y-2">
                                                                        {excelImportResult.customerDuplicatesList && excelImportResult.customerDuplicatesList.length > 0 && (
                                                                            <div>
                                                                                <p className={`text-xs font-semibold mb-1 ${isDark ? 'text-blue-300' : 'text-blue-700'}`}>Kunden-Duplikate:</p>
                                                                                <ul className={`space-y-1 text-xs max-h-40 overflow-y-auto ${isDark ? 'text-blue-200' : 'text-blue-800'}`}>
                                                                                    {excelImportResult.customerDuplicatesList.map((dup: any, idx: number) => (
                                                                                        <li key={idx}>Zeile {dup.row}: {dup.customer} - {dup.reason}</li>
                                                                                    ))}
                                                                                </ul>
                                                                            </div>
                                                                        )}
                                                                        {excelImportResult.contractDuplicatesList && excelImportResult.contractDuplicatesList.length > 0 && (
                                                                            <div>
                                                                                <p className={`text-xs font-semibold mb-1 ${isDark ? 'text-blue-300' : 'text-blue-700'}`}>Vertrags-Duplikate:</p>
                                                                                <ul className={`space-y-1 text-xs max-h-40 overflow-y-auto ${isDark ? 'text-blue-200' : 'text-blue-800'}`}>
                                                                                    {excelImportResult.contractDuplicatesList.map((dup: any, idx: number) => (
                                                                                        <li key={idx}>Zeile {dup.row}: {dup.customer} - {dup.contract} ({dup.reason})</li>
                                                                                    ))}
                                                                                </ul>
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                </details>
                                                            )}
                                                        </div>
                                                    )}
                                                    {!isImporting && !isUploadingExcel && syncStatus === 'success' && lastSynced && <p>Zuletzt synchronisiert: {new Date(lastSynced).toLocaleString('de-DE')}</p>}
                                                </div>
                                            )}
                                        </PageCard>
                                    </PageLayout>
                                )}
                                {/* Team, Ziele, Geburtstage, Abläufe, Provisionen, Aufgaben, Vorlagen, Protokoll – gleiche Breite wie Dienstplan (max-w-5xl) */}
                                {currentView === 'users' && (<TeamView users={users} currentUser={currentUser} onEditUser={(u: any) => {
                                    const targetId = u?.id;
                                    const isAdmin = currentUser?.role === 'admin';
                                    if (!isAdmin && targetId && targetId !== currentUser?.id) {
                                        showToast('Du kannst nur dein eigenes Profil bearbeiten.', 'error');
                                        return;
                                    }
                                    if (!isAdmin && !targetId) {
                                        showToast('Du kannst nur dein eigenes Profil bearbeiten.', 'error');
                                        return;
                                    }
                                    setUserFormData(u);
                                    setEditingUserAllowedToView(u.allowedToView || []);
                                    setShowUserModal(true);
                                }} onDeleteUser={(id: any) => requestConfirm('Mitarbeiter wirklich löschen?', () => deleteUser(id), { variant: 'danger', title: 'Mitarbeiter löschen', confirmText: 'Löschen' })} onToggleVisibility={toggleUserVisibility} onOpenPermissions={openPermissionsModal} onCleanup={() => requestConfirm('Duplikate bereinigen?', cleanupDuplicates, { title: 'Duplikate bereinigen', confirmText: 'Ja, bereinigen' })} />)}
                                {currentView === 'goals' && <GoalsView goals={goals} users={activeUsers} currentUser={currentUser} onEditGoal={openGoalModal} onSaveGoal={saveGoalDirectly} />}
                                {currentView === 'birthdays' && (
                                    <BirthdaysView
                                        customers={rawCustomers}
                                        currentUser={currentUser}
                                        allUsers={users}
                                        onSelectCustomer={setSelectedCustomer}
                                        onNavigate={setCurrentView}
                                    />
                                )}
                                {currentView === 'expirations' && <ExpirationView customers={rawCustomers} currentUser={currentUser} allUsers={users} contractStates={contractStates} onToggleContractState={toggleContractStatus} onSelectCustomer={setSelectedCustomer} showToast={showToast} />}
                                {currentView === 'verlaengerung' && <VerlaengerungView customers={rawCustomers} currentUser={currentUser} allUsers={users} contractStates={contractStates} onToggleContractState={toggleContractStatus} onSelectCustomer={setSelectedCustomer} showToast={showToast} />}
                                {currentView === 'passwords' && (
                                    <PasswordsView
                                        passwords={passwords}
                                        currentUser={currentUser}
                                        onSavePassword={savePassword}
                                        onDeletePassword={delPwd}
                                    />
                                )}
                                {currentView === 'provisionen' && <ProvisionsView currentUser={currentUser ?? undefined} initialProvisionRules={provisionRulesFromFirebase} onSaveProvisionRules={saveProvisionRules} />}
                                {currentView === 'tasks' && <TasksListView tasks={tasks} currentUser={currentUser} onUpdateTask={updateTask} onDeleteTask={deleteTask} onShowTaskModal={setShowTaskModal} allUsers={users} />}
                                {currentView === 'templates' && currentUser?.role === 'admin' && <TemplateManagerView templates={letterTemplates} onSaveTemplate={saveTemplate} onDeleteTemplate={deleteTemplate} currentUser={currentUser} />}
                                {currentView === 'stats' && (currentUser?.role === 'admin' || (currentUser as any)?.canViewStats) && (
                                    <StatsView
                                        users={users}
                                        logs={logs}
                                        customers={rawCustomers}
                                        tasks={tasks}
                                        contractStates={contractStates}
                                        currentUser={currentUser}
                                    />
                                )}
                                {currentView === 'boni_tables' && currentUser?.role === 'admin' && <BoniTabellenAdminView currentUser={currentUser} onLoadBoniTables={loadBoniTables} onSaveBoniTables={saveBoniTables} />}
                                {currentView === 'activity' && <ActivityLogView logs={logs} />}
                                {currentView === 'settings' && currentUser && (
                                    <SettingsView
                                        user={currentUser}
                                        onEditUser={(u: any) => {
                                            setUserFormData(u || currentUser);
                                            setShowUserModal(true);
                                        }}
                                    />
                                )}
                            </div>
                        </div>
                    </main>
                        {/* Rechtes Detail-Panel (Salesforce-Style) */}
                        {selectedCustomer && !isMobile && (
                            <CustomerDetailPanel
                                customer={selectedCustomer}
                                onClose={() => setSelectedCustomer(null)}
                                isDark={isDark}
                                onOpenFullView={() => setShowCustomerDetailModal(true)}
                            />
                        )}
                    </div>
                </div>

                {/* Goal Modal */}
                {showGoalModal && (
                    <div className={`fixed inset-0 z-[70] flex items-end sm:items-center justify-center p-0 sm:p-4 ${isDark ? 'bg-gray-900/60' : 'bg-slate-900/10'} backdrop-blur-sm`}>
                        <div className={`${isDark ? 'glass-card text-white' : 'bg-white text-slate-900 border border-slate-200 shadow-2xl'} rounded-t-3xl sm:rounded-3xl p-6 sm:p-8 w-full max-w-md max-h-[90dvh] overflow-y-auto animate-scale-in`} style={{ paddingBottom: 'max(1.5rem, env(safe-area-inset-bottom))' }}>
                            <h3 className="text-2xl font-bold mb-4 flex items-center gap-2"><Target className="text-green-500" /> Zielvereinbarung {new Date().getFullYear()}</h3>
                            <div className="mb-4"><p className={`${isDark ? 'text-white/70' : 'text-slate-700'} text-sm font-bold`}>Mitarbeiter: <span className="text-blue-600">{goalFormData.userName}</span></p></div>
                            <form onSubmit={e => { e.preventDefault(); saveGoal(); }}>
                                <div className="space-y-4">
                                    <div className={`${isDark ? 'bg-green-500/15 border-green-400/30' : 'bg-green-50 border-green-100'} p-4 rounded-xl border`}>
                                        <label className="text-xs font-bold text-green-600 uppercase mb-2 block">Jahresziel (WS)</label>
                                        <IOSInput name="target" type="number" step="0.01" placeholder="z.B. 50000" value={goalFormData.target} onChange={(e: any) => setGoalFormData({ ...goalFormData, target: e.target.value })} required />
                                    </div>
                                    <div className={`${isDark ? 'bg-blue-500/15 border-blue-400/30' : 'bg-blue-50 border-blue-100'} p-4 rounded-xl border`}>
                                        <label className="text-xs font-bold text-blue-600 uppercase mb-2 block">Aktueller Stand (WS)</label>
                                        <IOSInput name="current" type="number" step="0.01" placeholder="z.B. 35000" value={goalFormData.current} onChange={(e: any) => setGoalFormData({ ...goalFormData, current: e.target.value })} required />
                                    </div>
                                    <div>
                                        <label className={`${isDark ? 'text-white/70' : 'text-slate-600'} text-xs font-bold uppercase mb-2 block`}>Beschreibung (optional)</label>
                                        <textarea name="description" className={`${isDark ? 'bg-white/10 text-white placeholder-white/50' : 'bg-slate-100 text-slate-900 placeholder-slate-500'} w-full border-0 rounded-xl px-4 py-3 text-sm outline-none`} rows={2} placeholder="z.B. Umsatzziel WS..." value={goalFormData.description} onChange={(e: any) => setGoalFormData({ ...goalFormData, description: e.target.value })}></textarea>
                                    </div>
                                    {goalFormData.target && goalFormData.current && (
                                        <div className={`${isDark ? 'bg-white/5' : 'bg-gray-50'} p-4 rounded-xl`}>
                                            <div className="flex justify-between items-center mb-2">
                                                <span className={`${isDark ? 'text-white/70' : 'text-slate-600'} text-xs font-bold`}>Zielerreichung</span>
                                                <span className={`text-2xl font-bold ${(parseFloat(goalFormData.current) / parseFloat(goalFormData.target) * 100) >= 100 ? 'text-green-600' : (parseFloat(goalFormData.current) / parseFloat(goalFormData.target) * 100) >= 75 ? 'text-blue-600' : 'text-orange-600'}`}>{Math.round((parseFloat(goalFormData.current) / parseFloat(goalFormData.target)) * 100)}%</span>
                                            </div>
                                            <div className="bg-gray-200 rounded-full h-3 overflow-hidden">
                                                <div className={`h-full transition-all duration-300 ${(parseFloat(goalFormData.current) / parseFloat(goalFormData.target) * 100) >= 100 ? 'bg-green-500' : (parseFloat(goalFormData.current) / parseFloat(goalFormData.target) * 100) >= 75 ? 'bg-blue-500' : 'bg-orange-500'}`} style={{ width: `${Math.min((parseFloat(goalFormData.current) / parseFloat(goalFormData.target)) * 100, 100)}%` }}></div>
                                            </div>
                                        </div>
                                    )}
                                    <div className="flex gap-3 pt-2">
                                        <IOSButton className="flex-1" variant="ghost" onClick={() => { setShowGoalModal(false); setGoalFormData({}); }}>Abbrechen</IOSButton>
                                        <IOSButton className="flex-1" type="submit">Speichern</IOSButton>
                                    </div>
                                </div>
                            </form>
                        </div>
                    </div>
                )}
            </div>

            {/* Modals - Special */}
            {showPermissionsModal && permissionsTargetUser && (<div className={`fixed inset-0 z-[70] flex items-center justify-center p-4 ${isDark ? 'bg-gray-900/60' : 'bg-slate-900/10'} backdrop-blur-sm`}><div className={`${isDark ? 'glass-card text-white' : 'bg-white text-slate-900 border border-slate-200 shadow-2xl'} rounded-3xl p-8 w-full max-w-md animate-scale-in`}><h3 className="text-xl font-bold mb-4">Sichtbarkeit für {permissionsTargetUser.name}</h3><p className={`${isDark ? 'text-white/60' : 'text-slate-600'} text-sm mb-4`}>Wähle Mitarbeiter, deren Kunden dieser Nutzer sehen darf:</p><div className="space-y-2 max-h-60 overflow-y-auto mb-4">{activeUsers.filter((u: any) => u.id !== permissionsTargetUser.id).map((u: any) => { const isAllowed = permissionsTargetUser.allowedToView?.includes(u.id); return (<div key={u.id} onClick={() => togglePermission(u.id)} className={`flex items-center gap-3 p-3 rounded-xl cursor-pointer border transition-all ${isAllowed ? 'bg-cyan-500/15 border-cyan-400/30 text-cyan-800' : isDark ? 'bg-white/10 border-white/20 hover:bg-white/15 text-white' : 'bg-slate-50 border-slate-200 hover:bg-slate-100 text-slate-900'}`}>{isAllowed ? <CheckCircle className="text-cyan-500" size={20} /> : <Circle className={`${isDark ? 'text-white/40' : 'text-slate-400'}`} size={20} />}<span className="font-semibold">{u.name}</span></div>); })}{activeUsers.length <= 1 && <p className={`${isDark ? 'text-white/40' : 'text-slate-500'} text-center text-sm`}>Keine anderen Mitarbeiter verfügbar.</p>}</div><div className="flex justify-end"><IOSButton onClick={() => setShowPermissionsModal(false)}>Fertig</IOSButton></div></div></div>)}
            {/* User / Mitarbeiter bearbeiten Modal */}
            {
                showUserModal && (
                    <div className={`fixed inset-0 z-[70] flex items-center justify-center p-4 ${isDark ? 'bg-gray-900/60' : 'bg-slate-900/10'} backdrop-blur-sm`} onClick={() => setShowUserModal(false)}>
                        <div className="max-w-md w-full" onClick={(e) => e.stopPropagation()}>
                            <EmployeeEditForm
                                initial={userFormData}
                                onSubmit={(values) => saveUser({ ...values, id: userFormData?.id })}
                                onCancel={() => setShowUserModal(false)}
                            />
                        </div>
                    </div>
                )
            }
            {/* Aufgabe erstellen Modal */}
            {
                showTaskModal && (
                    <div className={`fixed inset-0 z-[70] flex items-center justify-center p-4 ${isDark ? 'bg-gray-900/60' : 'bg-slate-900/10'} backdrop-blur-sm`} onClick={() => { setShowTaskModal(false); setNewTaskDefaults({}); }}>
                        <div className={`${isDark ? 'glass-card text-white' : 'bg-white text-slate-900 border border-slate-200 shadow-2xl'} rounded-3xl p-8 w-full max-w-md animate-scale-in`} onClick={(e) => e.stopPropagation()}>
                            <div className="flex justify-between items-center mb-4">
                                <h3 className={`text-xl font-bold flex items-center gap-2 ${isDark ? 'text-white' : 'text-slate-900'}`}><CheckCircle size={22} /> Neue Aufgabe</h3>
                                <button type="button" onClick={() => { setShowTaskModal(false); setNewTaskDefaults({}); }} className={`${isDark ? 'bg-white/10 hover:bg-white/20' : 'bg-slate-100 hover:bg-slate-200'} p-2 rounded-full`}><X size={20} className={isDark ? 'text-white' : 'text-slate-900'} /></button>
                            </div>
                            <form onSubmit={(e) => { e.preventDefault(); if (!taskFormTitle.trim()) return; addTask({ title: taskFormTitle.trim(), description: taskFormDescription.trim() || undefined, assignedTo: taskFormAssignedTo || undefined }); setShowTaskModal(false); setNewTaskDefaults({}); }}>
                                <div className="space-y-4">
                                    <div>
                                        <label className={`block text-sm font-semibold mb-1.5 ${isDark ? 'text-white/80' : 'text-slate-700'}`}>Titel *</label>
                                        <input type="text" value={taskFormTitle} onChange={(e) => setTaskFormTitle(e.target.value)} placeholder="z. B. Kunde anrufen" className={`w-full px-4 py-2.5 rounded-xl border ${isDark ? 'bg-white/10 border-white/20 text-white placeholder:text-white/40' : 'bg-slate-50 border-slate-200 text-slate-900'}`} required autoFocus />
                                    </div>
                                    <div>
                                        <label className={`block text-sm font-semibold mb-1.5 ${isDark ? 'text-white/80' : 'text-slate-700'}`}>Beschreibung (optional)</label>
                                        <textarea value={taskFormDescription} onChange={(e) => setTaskFormDescription(e.target.value)} placeholder="Details zur Aufgabe" rows={3} className={`w-full px-4 py-2.5 rounded-xl border resize-none ${isDark ? 'bg-white/10 border-white/20 text-white placeholder:text-white/40' : 'bg-slate-50 border-slate-200 text-slate-900'}`} />
                                    </div>
                                    <div>
                                        <label className={`block text-sm font-semibold mb-1.5 ${isDark ? 'text-white/80' : 'text-slate-700'}`}>Zuständig</label>
                                        <select value={taskFormAssignedTo} onChange={(e) => setTaskFormAssignedTo(e.target.value)} className={`w-full px-4 py-2.5 rounded-xl border ${isDark ? 'bg-white/10 border-white/20 text-white' : 'bg-slate-50 border-slate-200 text-slate-900'}`}>
                                            <option value="Alle">Alle</option>
                                            {users.map((u: any) => <option key={u.id} value={u.name}>{u.name}</option>)}
                                        </select>
                                    </div>
                                </div>
                                <div className="flex gap-3 mt-6">
                                    <button type="button" onClick={() => { setShowTaskModal(false); setNewTaskDefaults({}); }} className={`flex-1 py-2.5 rounded-xl font-semibold ${isDark ? 'bg-white/10 hover:bg-white/20 text-white' : 'bg-slate-200 hover:bg-slate-300 text-slate-800'}`}>Abbrechen</button>
                                    <button type="submit" className={`flex-1 py-2.5 rounded-xl font-semibold ${isDark ? 'bg-cyan-500 hover:bg-cyan-600 text-white' : 'bg-cyan-600 hover:bg-cyan-700 text-white'}`}>Aufgabe erstellen</button>
                                </div>
                            </form>
                        </div>
                    </div>
                )
            }
            {showBirthdayModal && (<div className={`fixed inset-0 z-[70] flex items-center justify-center p-4 ${isDark ? 'bg-gray-900/60' : 'bg-slate-900/10'} backdrop-blur-sm`}><div className={`${isDark ? 'glass-card text-white' : 'bg-white text-slate-900 border border-slate-200 shadow-2xl'} rounded-3xl p-8 w-full max-w-lg animate-scale-in max-h-[80vh] flex flex-col`}><div className="flex justify-between items-center mb-4"><h3 className={`text-2xl font-bold flex items-center gap-2 ${isDark ? 'text-white' : 'text-slate-900'}`}><Calendar className="text-amber-500" /> Geburtstage</h3><button onClick={() => setShowBirthdayModal(false)} className={`${isDark ? 'bg-white/10 hover:bg-white/20' : 'bg-slate-100 hover:bg-slate-200'} p-2 rounded-full`}><X size={20} className={isDark ? 'text-white' : 'text-slate-900'} /></button></div>{letterTemplates.filter((t: any) => t.type === 'birthday').length > 0 && (<div className={`mb-4 pb-4 border-b ${isDark ? 'border-white/20' : 'border-slate-200'}`}><button onClick={() => { if (selectedCustomersForLetters.length === 0) { alert('Bitte wähle mindestens einen Kunden aus'); return; } setShowLetterGenerator(true); }} className="glass-btn-primary w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl font-semibold transition-colors disabled:opacity-50" disabled={selectedCustomersForLetters.length === 0}><FileText size={18} /> {selectedCustomersForLetters.length > 0 ? `Briefe für ${selectedCustomersForLetters.length} Kunden erstellen` : 'Kunden auswählen'}</button></div>)}<div className="flex-1 overflow-y-auto space-y-2">{allUpcomingBirthdays.length === 0 && <p className={`${isDark ? 'text-white/40' : 'text-slate-500'} text-center py-4`}>Keine Geburtstage in den nächsten 30 Tagen.</p>}{allUpcomingBirthdays.map((c: any) => (<div key={c.id} className={`flex items-center gap-3 p-3 rounded-xl transition-colors ${isDark ? 'bg-white/10 hover:bg-white/15' : 'bg-slate-50 hover:bg-slate-100 border border-slate-200'}`}><input type="checkbox" checked={selectedCustomersForLetters.some((sc: any) => sc.id === c.id)} onChange={(e) => { if (e.target.checked) { setSelectedCustomersForLetters([...selectedCustomersForLetters, c]); } else { setSelectedCustomersForLetters(selectedCustomersForLetters.filter((sc: any) => sc.id !== c.id)); } }} className={`w-5 h-5 rounded border ${isDark ? 'border-white/30 text-cyan-500 focus:ring-cyan-500' : 'border-slate-300 text-cyan-600 focus:ring-cyan-600'} cursor-pointer`} /><div onClick={() => { setShowBirthdayModal(false); setSelectedCustomer(c); }} className="flex-1 flex justify-between items-center cursor-pointer"><span className="font-medium">{c.vorname} {c.name}</span><span className={`${isDark ? 'text-amber-300 bg-amber-500/20' : 'text-amber-700 bg-amber-100'} font-bold px-2 py-1 rounded-lg text-xs`}>{c.gebDatum}</span></div></div>))}</div></div></div>)}
            {showExpirationModal && (<div className={`fixed inset-0 z-[70] flex items-center justify-center p-4 ${isDark ? 'bg-gray-900/60' : 'bg-slate-900/10'} backdrop-blur-sm`}><div className={`${isDark ? 'glass-card text-white' : 'bg-white text-slate-900 border border-slate-200 shadow-2xl'} rounded-3xl p-8 w-full max-w-4xl animate-scale-in max-h-[85vh] flex flex-col`}><div className={`flex justify-between items-center mb-4 pb-4 border-b ${isDark ? 'border-white/20' : 'border-slate-200'}`}><div><h3 className={`text-2xl font-bold flex items-center gap-2 ${isDark ? 'text-white' : 'text-slate-900'}`}><AlertCircle className="text-red-500" /> Abläufe Management</h3><p className={`${isDark ? 'text-white/50' : 'text-slate-600'} text-xs`}>Verträge vom 01.01.2000 bis in die nächsten 5 Monate</p></div><button onClick={() => setShowExpirationModal(false)} className={`${isDark ? 'bg-white/10 hover:bg-white/20' : 'bg-slate-100 hover:bg-slate-200'} p-2 rounded-full`}><X size={20} className={isDark ? 'text-white' : 'text-slate-900'} /></button></div><div className="mb-4 flex gap-4 flex-wrap"><div className="flex gap-2"><button onClick={() => setExpTab('open')} className={`px-3 py-1 rounded-lg text-xs font-bold ${expTab === 'open' ? 'bg-red-500/20 text-red-500 border border-red-400/40' : isDark ? 'text-white/60 bg-white/10 border border-white/20' : 'text-slate-600 bg-slate-100 border border-slate-200'}`}>Offen</button><button onClick={() => setExpTab('done')} className={`px-3 py-1 rounded-lg text-xs font-bold ${expTab === 'done' ? 'bg-green-500/20 text-green-600 border border-green-400/40' : isDark ? 'text-white/60 bg-white/10 border border-white/20' : 'text-slate-600 bg-slate-100 border border-slate-200'}`}>Erledigt</button></div><div className={`flex gap-2 pl-4 items-center ${isDark ? 'border-white/20' : 'border-slate-200'} border-l`}><label className="flex items-center gap-2 text-xs"><span className={`${isDark ? 'text-white/60' : 'text-slate-600'} font-bold`}>Von:</span><input type="date" value={expDateFrom} onChange={(e) => setExpDateFrom(e.target.value)} className="glass-input px-2 py-1 rounded text-xs" /></label><label className="flex items-center gap-2 text-xs"><span className={`${isDark ? 'text-white/60' : 'text-slate-600'} font-bold`}>Bis:</span><input type="date" value={expDateTo} onChange={(e) => setExpDateTo(e.target.value)} className="glass-input px-2 py-1 rounded text-xs" /></label><button onClick={() => { setExpDateFrom(''); setExpDateTo(''); }} className={`${isDark ? 'text-white/50 hover:text-white/70' : 'text-slate-500 hover:text-slate-700'} text-xs font-bold`}>Zurücksetzen</button></div><select value={expSparteFilter} onChange={(e) => setExpSparteFilter(e.target.value)} className="glass-input px-3 py-1 rounded-lg text-xs font-bold"><option value="">Alle Sparten</option>{Array.from(new Set(rawCustomers.flatMap((c: any) => c.verträge).map((v: any) => v.sparte))).sort().filter((s: any) => s && !s.toLowerCase().match(/leben|kfz/)).map((sparte: any) => <option key={sparte} value={sparte}>{sparte}</option>)}</select></div><div className="flex-1 overflow-y-auto space-y-2">{displayedExpirations.length === 0 && <p className={`${isDark ? 'text-white/40' : 'text-slate-500'} text-center py-10`}>Keine Verträge in dieser Ansicht.</p>}{displayedExpirations.map((item: any, idx: any) => (<div key={idx} className={`flex justify-between items-center p-3 rounded-xl transition-all group ${isDark ? 'bg-white/10 border border-white/20 hover:bg-white/15' : 'bg-slate-50 border border-slate-200 hover:bg-slate-100'}`}><div onClick={() => { setShowExpirationModal(false); setSelectedCustomer(item.customer); }} className="flex-1 cursor-pointer"><div className="flex items-center gap-2 mb-1"><p className={`font-bold text-sm ${isDark ? 'text-white' : 'text-slate-900'}`}>{item.customer.vorname} {item.customer.name}</p><span className={`text-[10px] px-1.5 py-0.5 rounded ${isDark ? 'text-white/40 bg-white/10' : 'text-slate-600 bg-slate-100'}`}>{item.customer.zustMA}</span></div><div className={`flex gap-4 text-xs ${isDark ? 'text-white/60' : 'text-slate-600'}`}><span className={isDark ? 'text-white/80' : 'text-slate-800 font-medium'}>{item.contract.sparte}</span><span className="font-mono">{item.contract.vsnr}</span></div></div><div className="text-right flex items-center gap-4"><div><span className={`font-mono font-bold px-2 py-1 rounded-lg text-xs ${expTab === 'open' ? 'bg-red-500/10 text-red-500' : 'bg-green-500/10 text-green-600'}`}>{item.contract.ablauf}</span></div><button onClick={() => toggleContractStatus(item.id, item.status)} className={`w-8 h-8 rounded-full flex items-center justify-center transition-all ${expTab === 'open' ? 'bg-red-500/10 text-red-400 hover:bg-green-500 hover:text-white' : 'bg-green-500 text-white'}`}>{expTab === 'open' ? <CheckCircle size={16} /> : <RefreshCw size={16} />}</button></div></div>))}</div></div></div>)}

            {/* Customer Detail Modal (Vollständige Ansicht – nur wenn explizit geöffnet) */}
            {
                selectedCustomer && showCustomerDetailModal && (
                    <div className={`fixed inset-0 z-[70] flex items-end sm:items-center justify-center p-0 sm:p-4 ${isDark ? 'bg-gray-900/60' : 'bg-slate-900/10'} backdrop-blur-sm`} onClick={() => { setShowCustomerDetailModal(false); if (isMobile) setSelectedCustomer(null); }}>
                        <div className={`w-full max-w-5xl h-[100dvh] sm:h-[90vh] flex flex-col rounded-none sm:rounded-3xl animate-scale-in ${isDark ? 'glass-card text-white border border-slate-700 shadow-[0_0_0_2px_rgba(30,41,59,0.7)]' : 'bg-white text-slate-900 border border-slate-200 shadow-2xl'}`} onClick={(e) => e.stopPropagation()}
                            style={{ maxHeight: '100dvh' }}>
                            <div className={`p-6 flex flex-col md:flex-row justify-between items-start gap-3 ${isDark ? 'bg-white/10 border-b border-white/20' : 'bg-slate-50 border-b border-slate-200'}`} style={{ paddingTop: 'max(1rem, env(safe-area-inset-top))' }}>
                                <div className="flex gap-4 items-center flex-1 min-w-0">
                                    <div className="min-w-0">
                                        <h2 className={`text-xl sm:text-2xl font-bold truncate ${isDark ? 'text-white' : 'text-slate-900'}`}>{selectedCustomer.vorname} {selectedCustomer.name}</h2>
                                        <button
                                            onClick={() => {
                                                setNewTaskDefaults({ title: `Aufgabe für ${selectedCustomer.vorname} ${selectedCustomer.name}`, description: '' });
                                                setShowTaskModal(true);
                                            }}
                                            className={`${isDark ? 'bg-green-500/20 text-green-300 border border-green-400/30 hover:bg-green-500/30' : 'bg-emerald-100 text-emerald-800 border border-emerald-200 hover:bg-emerald-200'} mt-2 px-4 py-2 rounded-xl text-sm font-bold flex items-center gap-2 transition-colors`}
                                        >
                                            <CheckCircle size={16} /> Aufgabe erstellen
                                        </button>
                                    </div>
                                </div>
                                <button onClick={() => { setShowCustomerDetailModal(false); if (isMobile) setSelectedCustomer(null); }} className={`${isDark ? 'bg-white/10 hover:bg-white/20' : 'bg-slate-100 hover:bg-slate-200'} p-2 rounded-full`}>
                                    <X size={20} className={isDark ? 'text-white' : 'text-slate-900'} />
                                </button>
                            </div>

                            <div className="flex-1 overflow-y-auto p-4 sm:p-6 grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6 md:gap-8 custom-scrollbar" style={{ paddingBottom: 'max(1.5rem, env(safe-area-inset-bottom))', maxHeight: 'calc(100dvh - 96px)' }}>
                                <div className="space-y-6">
                                    <div>
                                        <h3 className={`text-xs font-bold uppercase mb-2 ${isDark ? 'text-white/50' : 'text-slate-600'}`}>Adresse & Kontakt</h3>
                                        <div className={`p-4 rounded-xl text-sm space-y-2 ${isDark ? 'bg-white/10 text-white/90' : 'bg-slate-100 text-slate-800'}`}>
                                            {(selectedCustomer.anrede === 'm' || selectedCustomer.anrede === 'w') && (
                                                <p><span className={isDark ? 'text-white/60' : 'text-slate-500'}>Anrede:</span> {selectedCustomer.anrede === 'm' ? 'Herr' : 'Frau'}</p>
                                            )}
                                            {(selectedCustomer.strasse || selectedCustomer.plz || selectedCustomer.ort) ? (
                                                <p><span className={isDark ? 'text-white/60' : 'text-slate-500'}>Adresse:</span><br />{[selectedCustomer.strasse, [selectedCustomer.plz, selectedCustomer.ort].filter(Boolean).join(' ')].filter(Boolean).join(', ')}</p>
                                            ) : null}
                                            {selectedCustomer.telefon ? (
                                                <p><span className={isDark ? 'text-white/60' : 'text-slate-500'}>Telefon:</span> <a href={`tel:${selectedCustomer.telefon}`} className={isDark ? 'text-cyan-300 hover:underline' : 'text-blue-600 hover:underline'}>{selectedCustomer.telefon}</a></p>
                                            ) : null}
                                            {selectedCustomer.handy ? (
                                                <p><span className={isDark ? 'text-white/60' : 'text-slate-500'}>Handy:</span> <a href={`tel:${selectedCustomer.handy}`} className={isDark ? 'text-cyan-300 hover:underline' : 'text-blue-600 hover:underline'}>{selectedCustomer.handy}</a></p>
                                            ) : null}
                                            {/* E-Mail: Nur Anzeige, keine Bearbeitung mehr für Admins */}
                                            {selectedCustomer.mail ? (
                                                <div className="text-xs mt-1 text-slate-500 truncate" style={{ color: isDark ? '#38bdf8' : '#0e7490' }}>
                                                    E-Mail-Adresse: <span className="font-mono break-all">{selectedCustomer.mail}</span>
                                                </div>
                                            ) : null}
                                            {selectedCustomer.adp ? <p><span className={isDark ? 'text-white/60' : 'text-slate-500'}>ADP:</span> {selectedCustomer.adp}</p> : null}
                                            {selectedCustomer.btnr ? <p><span className={isDark ? 'text-white/60' : 'text-slate-500'}>Btnr:</span> {selectedCustomer.btnr}</p> : null}
                                            {selectedCustomer.gebDatum ? <p><span className={isDark ? 'text-white/60' : 'text-slate-500'}>Geburtsdatum:</span> {selectedCustomer.gebDatum}</p> : null}
                                            {!selectedCustomer.strasse && !selectedCustomer.plz && !selectedCustomer.ort && !selectedCustomer.telefon && !selectedCustomer.handy && !selectedCustomer.mail && !selectedCustomer.adp && !selectedCustomer.btnr && !selectedCustomer.gebDatum && (
                                                <p className={isDark ? 'text-white/50' : 'text-slate-500'}>Keine Kontaktdaten hinterlegt.</p>
                                            )}
                                        </div>
                                    </div>
                                    <div>
                                        <h3 className={`text-xs font-bold uppercase mb-2 ${isDark ? 'text-white/50' : 'text-slate-600'}`}>Versicherte Sparten</h3>
                                        <div className="flex flex-wrap gap-2">
                                            {Array.from(new Set(selectedCustomer.verträge?.map((v: any) => v.sparte).filter(Boolean) || [])).length > 0 ? (
                                                Array.from(new Set(selectedCustomer.verträge?.map((v: any) => v.sparte).filter(Boolean) || [])).map((sparte: any, idx: number) => (
                                                    <span key={idx} className={`text-xs font-semibold px-3 py-1.5 rounded-full border ${isDark ? 'bg-gradient-to-br from-blue-500/20 to-indigo-500/20 text-blue-200 border-blue-400/30' : 'bg-blue-50 text-blue-800 border-blue-200'}`}>
                                                        {sparte}
                                                    </span>
                                                ))
                                            ) : (
                                                <span className={`${isDark ? 'text-white/50' : 'text-slate-500'} text-sm`}>Keine Sparten vorhanden</span>
                                            )}
                                        </div>
                                    </div>

                                    {analyzeGaps(selectedCustomer.verträge || selectedCustomer.vertraege || []).length > 0 && (
                                        <div>
                                            <h3 className={`text-xs font-bold uppercase mb-2 ${isDark ? 'text-white/50' : 'text-slate-600'}`}>Potenziale</h3>
                                            <div className="flex flex-wrap gap-2">
                                                {analyzeGaps(selectedCustomer.verträge || selectedCustomer.vertraege || []).map((g: any) => <span key={g} className={`${isDark ? 'bg-purple-500/20 text-purple-200 border-purple-400/40' : 'bg-purple-50 text-purple-800 border-purple-200'} text-xs px-2 py-1 rounded-full font-semibold border`}>{g}</span>)}
                                            </div>
                                        </div>
                                    )}

                                    <div className="flex gap-2 flex-wrap">
                                        <button
                                            onClick={() => window.open(`mailto:${selectedCustomer.mail}?subject=Ihre Versicherungsangelegenheiten`)}
                                            className={`${isDark ? 'bg-cyan-500/20 text-cyan-300 hover:bg-cyan-500/30 border-cyan-400/30' : 'bg-cyan-600 text-white hover:bg-cyan-700 border-cyan-600'} px-3 py-2 rounded-xl text-xs font-bold flex items-center gap-2 border transition-colors`}
                                        >
                                            <Mail size={14} /> Email
                                        </button>
                                        <button onClick={() => {
                                            const customerNotes = notesMap[selectedCustomer.id] || [];
                                            if (customerNotes.length > 0) {
                                                setPdfNotesCustomer(selectedCustomer);
                                                setPdfNotesData(customerNotes);
                                                setShowPdfNotesDialog(true);
                                            } else {
                                                generatePDF(selectedCustomer, [], false);
                                            }
                                        }} className={`${isDark ? 'bg-white/10 text-white hover:bg-white/15 border-white/20' : 'bg-slate-200 text-slate-900 hover:bg-slate-300 border-slate-300'} px-3 py-2 rounded-xl text-xs font-bold flex items-center gap-2 border transition-colors`}>
                                            <Printer size={14} /> PDF
                                        </button>
                                        <button
                                            onClick={() => generateWordDoc('blanko', selectedCustomer, currentUser)}
                                            className={`${isDark ? 'bg-white/10 text-white hover:bg-white/15 border-white/20' : 'bg-slate-200 text-slate-900 hover:bg-slate-300 border-slate-300'} px-3 py-2 rounded-xl text-xs font-bold flex items-center gap-2 border transition-colors`}
                                        >
                                            <FileText size={14} /> Word
                                        </button>
                                    </div>
                                </div>

                                <div className="md:col-span-2 space-y-6">
                                    <div>
                                        <h3 className={`text-xs font-bold uppercase mb-2 ${isDark ? 'text-white/50' : 'text-slate-600'}`}>Verträge ({(selectedCustomer.verträge || selectedCustomer.vertraege || []).length})</h3>
                                        <div className="space-y-3">
                                            {(selectedCustomer.verträge || selectedCustomer.vertraege || []).map((v: any, idx: any) => {
                                                const daysUntil = getDaysUntilExpiry(v.ablauf);
                                                const urgency = getExpiryUrgencyStyle(daysUntil);
                                                const safeId = (v.vsnr || '').replace(/\//g, '_');
                                                const isDone = contractStates[safeId] === 'done';
                                                return (
                                                    <div key={idx} className={`p-4 rounded-xl shadow-sm hover:shadow-lg transition-all border ${isDark ? 'bg-white/10 border-white/20' : 'bg-slate-50 border-slate-200'} ${!isDone ? urgency.border : ''} ${!isDone ? urgency.bg : ''}`}>
                                                        <div className="flex justify-between items-start mb-2">
                                                            <div className="flex items-center gap-2">
                                                                <div className={`w-8 h-8 rounded-lg flex items-center justify-center font-bold text-xs ${isDark ? 'bg-white/10 text-white/60' : 'bg-slate-200 text-slate-700'}`}>{v.sparte.substring(0, 2).toUpperCase()}</div>
                                                                <div>
                                                                    <p className={`font-bold text-sm ${isDark ? 'text-white' : 'text-slate-900'}`}>{v.sparte}</p>
                                                                    <p className={`text-xs ${isDark ? 'text-white/60' : 'text-slate-600'}`}>{v.gesellschaft} | {v.vsnr}</p>
                                                                </div>
                                                            </div>
                                                            <div className="text-right flex flex-col items-end gap-1">
                                                                {v.mob && (
                                                                    <div className="flex items-center gap-1">
                                                                        <span className={`text-[9px] font-bold ${isDark ? 'text-white/60' : 'text-slate-600'}`}>MOB</span>
                                                                        <span className={`font-mono text-sm font-bold px-2 py-0.5 rounded whitespace-nowrap ${isDark ? 'bg-green-500/20 text-green-300' : 'bg-emerald-100 text-emerald-800'}`}>{formatMoney(v.mob)}</span>
                                                                    </div>
                                                                )}
                                                                {v.beitrag && (
                                                                    <div className="flex items-center gap-1">
                                                                        <span className={`text-[9px] font-bold ${isDark ? 'text-white/60' : 'text-slate-600'}`}>JAB</span>
                                                                        <span className={`font-mono text-xs font-semibold px-2 py-0.5 rounded whitespace-nowrap ${isDark ? 'bg-white/10 text-white/80' : 'bg-slate-200 text-slate-800'}`}>{formatMoney(v.beitrag)}</span>
                                                                    </div>
                                                                )}
                                                                {v.zahlweise && v.zahlweise !== '-' && (
                                                                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold whitespace-nowrap ${isDark ? 'bg-cyan-500/20 text-cyan-300' : 'bg-cyan-100 text-cyan-700'}`}>{v.zahlweise}</span>
                                                                )}
                                                            </div>
                                                        </div>
                                                        <div className={`flex justify-between items-center text-xs mt-2 pt-2 border-t ${isDark ? 'text-white/60 border-white/10' : 'text-slate-600 border-slate-200'}`}>
                                                            <div>
                                                                <span>Laufzeit: {v.beginn} - <span className={`font-bold ${isDone ? (isDark ? 'text-white/80' : 'text-slate-800') : urgency.text}`}>{v.ablauf}</span></span>
                                                                {!isDone && daysUntil < 0 ? <span className="ml-2 text-red-500 font-bold">⚠️ Überfällig {Math.abs(daysUntil)}d</span> : !isDone && daysUntil <= 30 ? <span className={`ml-2 font-bold ${urgency.text}`}>{daysUntil} Tage</span> : null}
                                                            </div>
                                                            <button onClick={() => toggleContractStatus(safeId, isDone ? 'done' : 'open')} className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 shadow-sm ${isDone ? 'bg-green-500 text-white hover:bg-green-600' : 'bg-blue-600 text-white hover:bg-blue-700'}`}>
                                                                {isDone ? <><CheckCircle size={14} /> Verlängert</> : <><RefreshCw size={14} /> Als verlängert markieren</>}
                                                            </button>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>

                                    <div>
                                        <h3 className={`text-xs font-bold uppercase mb-2 ${isDark ? 'text-white/50' : 'text-slate-600'}`}>Notizen</h3>
                                        <div className={`${isDark ? 'bg-amber-500/20 border-amber-400/30 text-white' : 'bg-amber-50 border-amber-200 text-amber-900'} border rounded-xl p-4 min-h-[100px] mb-3 max-h-40 overflow-y-auto space-y-2`}>
                                            {(notesMap[selectedCustomer.id] || []).length === 0 && <p className={`${isDark ? 'text-white/70' : 'text-amber-800'} text-xs italic`}>Keine Notizen vorhanden.</p>}
                                            {(notesMap[selectedCustomer.id] || []).map((n: any, i: any) => {
                                                const noteDate = n.date ?? (n.createdAt ? (typeof n.createdAt === 'string' ? new Date(n.createdAt).toLocaleDateString('de-DE') : '') : '');
                                                return (
                                                    <div key={i} className={`${isDark ? 'border-amber-400/30 text-white' : 'border-amber-200 text-amber-900'} text-xs border-b last:border-0 pb-1 mb-1`}>
                                                        <span className={`${isDark ? 'text-amber-200' : 'text-amber-700'} font-bold`}>{n.author ?? 'Unbekannt'}:</span> {n.text} {noteDate && <span className={`${isDark ? 'text-white/60' : 'text-amber-700/80'} text-[10px]`}>({noteDate})</span>}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                        <form onSubmit={e => { e.preventDefault(); const inp = (e.target as any).note; const val = inp?.value?.trim(); if (val && selectedCustomer?.id) { addNote(selectedCustomer.id, val); inp.value = ''; } }} className="flex gap-2">
                                            <input name="note" className={`flex-1 rounded-xl px-3 py-2 text-sm ${isDark ? 'glass-input' : 'bg-slate-50 border border-slate-200 text-slate-900'}`} placeholder="Notiz hinzufügen..." />
                                            <button type="submit" className="p-2 glass-btn-primary rounded-xl"><Plus size={16} /></button>
                                        </form>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )
            }

            {/* Letter Generator Modal */}
            {
                showLetterGenerator && (
                    <div className={`fixed inset-0 z-[80] flex items-center justify-center p-4 ${isDark ? 'bg-gray-900/60' : 'bg-slate-900/10'} backdrop-blur-sm`}>
                        <div className={`${isDark ? 'glass-card text-white' : 'bg-white text-slate-900 border border-slate-200 shadow-2xl'} rounded-3xl p-6 w-full max-w-5xl max-h-[90vh] overflow-y-auto`}>
                            <div className="flex justify-between items-center mb-6">
                                <h3 className={`text-2xl font-bold flex items-center gap-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                                    <FileText className="text-cyan-500" /> Geburtstagsbriefe ({selectedCustomersForLetters.length})
                                </h3>
                                <button onClick={() => {
                                    setShowLetterGenerator(false);
                                    setSelectedCustomersForLetters([]);
                                    setSelectedTemplateId('');
                                }} className={`${isDark ? 'bg-white/10 hover:bg-white/20' : 'bg-slate-100 hover:bg-slate-200'} p-2 rounded-full`}>
                                    <X size={20} className={isDark ? 'text-white' : 'text-slate-900'} />
                                </button>
                            </div>

                            <div className="mb-6 space-y-4">
                                <div className="flex gap-3 items-center">
                                    <label className={`text-sm font-bold min-w-[120px] ${isDark ? 'text-white/70' : 'text-slate-700'}`}>Vorlage wählen:</label>
                                    <select
                                        value={selectedTemplateId}
                                        onChange={(e) => setSelectedTemplateId(e.target.value)}
                                        className="glass-input flex-1 rounded-xl p-3 text-sm"
                                    >
                                        <option value="">-- Bitte Vorlage auswählen --</option>
                                        {letterTemplates.filter((t: any) => t.type === 'birthday').map((t: any) => (
                                            <option key={t.id} value={t.id}>{t.name}</option>
                                        ))}
                                    </select>
                                </div>

                                <div className="flex gap-3">
                                    <button
                                        type="button"
                                        onClick={openPreviewModal}
                                        disabled={isGeneratingLetters || !selectedTemplateId || selectedCustomersForLetters.length === 0}
                                        className="glass-btn flex-1 flex items-center justify-center gap-2 px-6 py-4 rounded-xl font-bold text-base transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                                    >
                                        <Eye size={20} />
                                        Vorschau anzeigen
                                    </button>

                                    <button
                                        type="button"
                                        onClick={generateLettersPdf}
                                        disabled={isGeneratingLetters}
                                        className="glass-btn-primary flex-[2] flex items-center justify-center gap-2 px-6 py-4 rounded-xl font-bold text-base transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                                    >
                                        {isGeneratingLetters ? <Loader2 className="animate-spin" size={20} /> : <Printer size={20} />}
                                        {isGeneratingLetters ? 'Erstelle PDF...' : 'PDF generieren'}
                                    </button>
                                </div>

                                {isGeneratingLetters && pdfGenerationProgress > 0 && (
                                    <div className={`${isDark ? 'bg-cyan-500/20 border-cyan-400/30' : 'bg-cyan-50 border-cyan-200'} p-4 rounded-xl border`}>
                                        <div className="flex justify-between items-center mb-2">
                                            <span className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-cyan-900'}`}>Fortschritt</span>
                                            <span className={`text-sm font-bold ${isDark ? 'text-cyan-300' : 'text-cyan-700'}`}>{pdfGenerationProgress}%</span>
                                        </div>
                                        <div className={`${isDark ? 'bg-white/20' : 'bg-cyan-100'} w-full rounded-full h-3 overflow-hidden`}>
                                            <div
                                                className="bg-cyan-500 h-full transition-all duration-300 ease-out"
                                                style={{ width: `${pdfGenerationProgress}%` }}
                                            ></div>
                                        </div>
                                        <p className={`${isDark ? 'text-cyan-200' : 'text-cyan-800'} text-xs mt-2`}>
                                            {Math.round((pdfGenerationProgress / 100) * selectedCustomersForLetters.length)} von {selectedCustomersForLetters.length} Briefen erstellt
                                        </p>
                                    </div>
                                )}
                            </div>

                            <div className={`${isDark ? 'bg-cyan-500/20 border-cyan-400/30 text-cyan-100' : 'bg-cyan-50 border-cyan-200 text-cyan-900'} border rounded-xl p-4 mb-4 text-sm`}>
                                <p className="font-bold mb-1">ℹ️ Hinweis</p>
                                <p>Wähle eine Vorlage aus und klicke auf den Button. Die Briefe werden automatisch mit den Kundendaten und allen Bildern/Logos ausgefüllt und als ein PDF (eine Seite pro Kunde) heruntergeladen. Bis zu 100 Kunden pro Batch.</p>
                            </div>

                            <div className="space-y-3">
                                <h4 className={`text-sm font-bold uppercase ${isDark ? 'text-white/60' : 'text-slate-600'}`}>Ausgewählte Kunden ({selectedCustomersForLetters.length}):</h4>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                    {selectedCustomersForLetters.map((c: any) => (
                                        <div key={c.id} className={`flex items-center justify-between p-3 rounded-xl border ${isDark ? 'bg-white/10 border-white/20' : 'bg-slate-50 border-slate-200'}`}>
                                            <div>
                                                <span className={`font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>{c.vorname} {c.name}</span>
                                                <span className={`text-xs ml-2 ${isDark ? 'text-white/60' : 'text-slate-600'}`}>{c.gebDatum}</span>
                                            </div>
                                            <button
                                                onClick={() => setSelectedCustomersForLetters(selectedCustomersForLetters.filter((sc: any) => sc.id !== c.id))}
                                                className={`${isDark ? 'text-red-300 hover:bg-red-500/20' : 'text-red-600 hover:bg-red-50'} p-1 rounded-lg`}
                                            >
                                                <X size={16} />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                )
            }

            {/* PDF Preview Modal */}
            {
                showPdfPreview && previewCustomer && (
                    <div className="fixed inset-0 z-[85] flex items-center justify-center p-4 bg-gray-900/60 backdrop-blur-sm">
                        <div className="glass-card rounded-3xl p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
                            <div className="flex justify-between items-center mb-6">
                                <h3 className="text-2xl font-bold flex items-center gap-2 text-white">
                                    <Eye className="text-cyan-500" /> Vorschau: Brief für {previewCustomer.vorname} {previewCustomer.name}
                                </h3>
                                <button onClick={() => {
                                    setShowPdfPreview(false);
                                    setPreviewCustomer(null);
                                }} className="p-2 bg-white/10 rounded-full hover:bg-white/20">
                                    <X size={20} className="text-white" />
                                </button>
                            </div>

                            <div className="bg-gray-50 p-6 rounded-xl">
                                <div
                                    className="bg-white p-12 shadow-lg mx-auto"
                                    style={{
                                        width: '210mm',
                                        minHeight: '297mm',
                                        maxWidth: '100%',
                                        boxSizing: 'border-box'
                                    }}
                                    dangerouslySetInnerHTML={{
                                        __html: fillTemplateContent(
                                            letterTemplates.find((t: any) => t.id === selectedTemplateId)?.content || '',
                                            previewCustomer
                                        )
                                    }}
                                />
                            </div>

                            <div className="mt-6 flex justify-end">
                                <button onClick={() => setShowPdfPreview(false)} className="glass-btn px-6 py-3 rounded-xl font-bold transition-colors">
                                    Schließen
                                </button>
                            </div>
                        </div>
                    </div>
                )
            }

            {/* PDF Notes Dialog Modal */}
            {
                showPdfNotesDialog && (
                    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999]">
                        <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl p-8 max-w-sm border border-white/10 shadow-2xl">
                            <h2 className="text-xl font-bold text-white mb-4">Interne Notizen</h2>
                            <p className="text-gray-300 mb-6">
                                Sollen die internen Notizen im PDF enthalten sein?
                            </p>
                            <div className="flex gap-3">
                                <button
                                    onClick={() => {
                                        generatePDF(pdfNotesCustomer, pdfNotesData, true);
                                        setShowPdfNotesDialog(false);
                                    }}
                                    className="flex-1 glass-btn-primary px-4 py-2 rounded-xl font-bold transition-colors"
                                >
                                    Ja, mit Notizen
                                </button>
                                <button
                                    onClick={() => {
                                        generatePDF(pdfNotesCustomer, [], false);
                                        setShowPdfNotesDialog(false);
                                    }}
                                    className="flex-1 glass-btn px-4 py-2 rounded-xl font-bold transition-colors"
                                >
                                    Nein
                                </button>

                            </div>
                        </div>
                    </div>
                )
            }
        </>
    );
}

export default App;
